import { Component, OnInit, Inject, DestroyRef, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin } from 'rxjs';
import { TimerecordsService } from '../../core/services/timerecords.service';
import { DialogService } from '../../core/services/dialog.service';
import { FileSaverService } from '../../core/services/file-saver.service';
import {
  BillableBudgetBreakdown,
  BillableBudgetCalculation,
  BillableBudgetInputs,
  BusinessYearMonthValueMap,
  BusinessYearMonthWorkHoursMap,
  DateHolidayService,
  MonthWorkHours,
} from '../../core/services/date-holiday.service';
import {
  ProjectSelectionDialogResult,
  ProjectSelectionModalComponent,
  Project,
} from '../../shared/components/modal/project-selection-modal.component';
import {
  HoursDrilldownModalComponent,
  HoursDrilldownRow,
} from '../../shared/components/modal/hours-drilldown-modal.component';
import moment from 'moment';
import { ModalService } from '../../core/services/modal.service';
import { HttpParams } from '@angular/common/http';
import * as XLSX from 'xlsx';
import { Costcenter } from '../../models/costcenter.model';
import { removeBlankStringCells } from '../../shared/utils/excel.utils';

interface OccupancyRow {
  employee_id: number;
  name: string;
  contractor?: string | null;
  costcenter?: string | null;
  business_area?: string | null;
  start_date?: string | null;
  stop_date?: string | null;
  jul: number | null;
  aug: number | null;
  sep: number | null;
  okt: number | null;
  nov: number | null;
  dec_: number | null;
  jan: number | null;
  feb: number | null;
  mar: number | null;
  apr: number | null;
  maj: number | null;
  jun: number | null;
  total: number | null;
}

interface KpiMonthFacts {
  active_in_month?: boolean;
  available_hours: number;
  budget_billable_hours: number;
  all_hours: number;
  bonus_hours: number;
  debit_hours: number;
  idle_hours: number;
  internal_comp_hours: number;
  internal_hours: number;
  internal_project_hours: number;
  oh_hours: number;
  sick_hours: number;
  leave_hours: number;
  vacation_hours: number;
  comp_out_hours: number;
  kitscon_hours: number;
  competence_hours: number;
  idle_group_hours: number;
  sales_marketing_hours: number;
  other_internal_hours: number;
  priced_debit_hours: number;
  debit_value: number;
  uninvoiced_debit_hours: number;
  uninvoiced_debit_value: number;
  invoiced_debit_hours: number;
  invoiced_debit_value: number;
  zero_price_debit_hours: number;
  zero_price_debit_value: number;
  avg_hour_rate: number | null;
  breakdown_hours_total?: number;
  breakdown_hours_difference?: number;
  breakdown_hours_ok?: boolean;
}

interface KpiFactsRow {
  employee_id: number;
  name: string;
  contractor?: string | null;
  costcenter?: string | null;
  business_area?: string | null;
  start_date?: string | null;
  stop_date?: string | null;
  months: Partial<Record<keyof BusinessYearMonthValueMap, KpiMonthFacts>>;
  totals: KpiMonthFacts;
}

interface KpiFactsPayload {
  startYear: number;
  endMonth: number;
  months: Array<keyof BusinessYearMonthValueMap>;
  rows: KpiFactsRow[];
}

interface HoursBreakdownCheckSummary {
  checkedEntries: number;
  mismatchEntries: number;
  maxDifferenceHours: number;
  totalDifferenceHours: number;
  ok: boolean;
}

interface KpiMonthMetrics {
  bonus_ratio?: number | null;
  utilization_gross?: number | null;
  utilization_net_absence?: number | null;
  utilization_budget?: number | null;
  budget_gap_hours?: number;
  internal_ratio?: number | null;
  absence_ratio?: number | null;
  idle_ratio?: number | null;
  oh_ratio?: number | null;
  internal_project_ratio?: number | null;
  invoiced_hours_ratio?: number | null;
  invoiced_value_ratio?: number | null;
  uninvoiced_hours_ratio?: number | null;
  uninvoiced_value_ratio?: number | null;
  zero_price_debit_ratio?: number | null;
  avg_hour_rate?: number | null;
  debit_value?: number;
  invoiced_debit_hours?: number;
  invoiced_debit_value?: number;
  uninvoiced_debit_hours?: number;
  uninvoiced_debit_value?: number;
  zero_price_debit_hours?: number;
  zero_price_debit_value?: number;
  value_per_available_hour?: number | null;
  value_per_net_hour?: number | null;
}

interface KpiMetricsRow {
  employee_id: number;
  name: string;
  contractor?: string | null;
  costcenter?: string | null;
  business_area?: string | null;
  start_date?: string | null;
  stop_date?: string | null;
  months: Partial<Record<keyof BusinessYearMonthValueMap, KpiMonthMetrics>>;
  totals: KpiMonthMetrics;
}

interface KpiMetricsPayload {
  startYear: number;
  endMonth: number;
  family: 'all' | 'capacity' | 'value' | 'pipeline';
  months: Array<keyof BusinessYearMonthValueMap>;
  rows: KpiMetricsRow[];
}

interface UninvoicedZeroPriceDebitRow {
  reporteddate: string;
  year: number;
  month: number;
  employee_id: number;
  name: string;
  costcenter?: string | null;
  business_area?: string | null;
  start_date?: string | null;
  stop_date?: string | null;
  customername: string | null;
  endcustomer?: string | null;
  project_id: number | null;
  project_name: string | null;
  activity_id: number;
  activity_name: string;
  price: number;
  invoice_status: string;
  hours: number;
  amount: number;
}

interface ZeroPriceCellItem {
  reporteddate: string;
  customername: string | null;
  endcustomer?: string | null;
  project_name: string | null;
  activity_name: string;
  price: number;
  invoice_status: string;
  hours: number;
  amount: number;
}

interface ZeroPriceCellSummaryItem {
  customername: string | null;
  endcustomer?: string | null;
  project_name: string | null;
  activity_name: string;
  price: number;
  invoice_status: string;
  hours: number;
  amount: number;
}

interface ZeroPriceMatrixRow {
  employee_id: number;
  name: string;
  costcenter?: string | null;
  business_area?: string | null;
  start_date?: string | null;
  stop_date?: string | null;
  jul: ZeroPriceCellItem[];
  aug: ZeroPriceCellItem[];
  sep: ZeroPriceCellItem[];
  okt: ZeroPriceCellItem[];
  nov: ZeroPriceCellItem[];
  dec_: ZeroPriceCellItem[];
  jan: ZeroPriceCellItem[];
  feb: ZeroPriceCellItem[];
  mar: ZeroPriceCellItem[];
  apr: ZeroPriceCellItem[];
  maj: ZeroPriceCellItem[];
  jun: ZeroPriceCellItem[];
  total: number;
}

interface DebitProjectSummaryRow {
  customer_id: number | null;
  customername: string | null;
  endcustomer?: string | null;
  project_id: number | null;
  project_name: string | null;
  hours: number;
  amount: number;
  employee_count: number;
  activity_count: number;
  first_reporteddate: string | null;
  last_reporteddate: string | null;
}

interface MixMatrixRow {
  customer_id: number | null;
  customername: string;
  endcustomer?: string | null;
  group_id: number | null;
  group_name: string;
  months: Partial<Record<keyof BusinessYearMonthValueMap, number | null>>;
  total: number | null;
}

interface EmployeeCostSalaryMonth {
  salary_record_id: number;
  salary_startdate: string;
  salary_stopdate: string | null;
  salary: number;
  bonus: number;
  overtime: number;
  cbonus: number;
  responsability: number;
  time: number;
}

interface EmployeeCostRow {
  employee_id: number;
  employee_number: number | null;
  name: string;
  contractor?: string | null;
  costcenter: string | null;
  business_area: string | null;
  start_date?: string | null;
  stop_date?: string | null;
  months: Partial<Record<keyof BusinessYearMonthValueMap, number | null>>;
  salary_months: Partial<Record<keyof BusinessYearMonthValueMap, EmployeeCostSalaryMonth | null>>;
  total: number | null;
}

interface EmployeeCostPayload {
  startYear: number;
  endMonth: number;
  months: Array<keyof BusinessYearMonthValueMap>;
  rows: EmployeeCostRow[];
}

interface AbsenceFacts {
  available_hours: number;
  sick_hours: number;
  vab_hours: number;
  leave_hours: number;
  vacation_hours: number;
  comp_out_hours: number;
  absence_hours: number;
  absence_ratio: number | null;
}

interface AbsenceRow {
  employee_id: number;
  name: string;
  contractor?: string | null;
  costcenter?: string | null;
  business_area?: string | null;
  start_date?: string | null;
  stop_date?: string | null;
  months: Partial<Record<keyof BusinessYearMonthValueMap, AbsenceFacts>>;
  totals: AbsenceFacts;
}

interface AbsencePayload {
  startYear: number;
  endMonth: number;
  months: Array<keyof BusinessYearMonthValueMap>;
  types: string[];
  rows: AbsenceRow[];
  totals: AbsenceFacts;
}

type OccupancyMode =
  | 'allHours'
  | 'debitHours'
  | 'sickHours'
  | 'leaveHours'
  | 'vacationHours'
  | 'compOutHours'
  | 'kitsconHours'
  | 'competenceHours'
  | 'internalProjectHours'
  | 'ohHours'
  | 'idleGroupHours'
  | 'salesMarketingHours'
  | 'otherInternalHours';

type UtilizationMode = 'gross' | 'netAfterAbsence' | 'againstBudget' | 'budgetGapHours';
type MixMode = 'value' | 'hours' | 'avgRate';
type CustomerMixView = 'invoiceCustomer' | 'endcustomer';
type MixSortDirection = 'asc' | 'desc';
type MixSortKey = keyof BusinessYearMonthValueMap | 'total' | 'customer';
type KpiMode =
  | 'internalRatio'
  | 'absenceRatio'
  | 'idleRatio'
  | 'ohRatio'
  | 'internalProjectRatio'
  | 'debitValue'
  | 'valuePerAvailableHour'
  | 'valuePerNetHour'
  | 'invoicedHoursRatio'
  | 'invoicedValueRatio'
  | 'uninvoicedHoursRatio'
  | 'uninvoicedValueRatio'
  | 'zeroPriceDebitRatio'
  | 'invoicedDebitHours'
  | 'invoicedDebitValue'
  | 'uninvoicedDebitHours'
  | 'uninvoicedDebitValue'
  | 'zeroPriceDebitHours';
type DeviationUnit = 'percent' | 'hours' | 'currency';
type OccupancyReportType =
  | 'bonus'
  | 'utilization'
  | 'hours'
  | 'averageRate'
  | 'zeroPriceDebit'
  | 'zeroPriceProjects'
  | 'customerMix'
  | 'projectMix'
  | 'employeeCost'
  | 'absenceHours'
  | 'kpi'
  | 'deviations';
type DebitDetailInvoiceState = 'uninvoiced' | 'invoiced' | 'all';
type DebitDetailPriceMode = 'zeroOrNull' | 'positive' | 'all';
type BudgetInputKey =
  | 'semester'
  | 'kitscon'
  | 'kompetensutveckling'
  | 'månadsmöten'
  | 'genomsnittSjuk';

interface DeviationRow {
  employee_id: number;
  name: string;
  monthKey: keyof BusinessYearMonthValueMap;
  monthName: string;
  value: number;
  threshold: number;
  difference: number;
}

interface DeviationSection {
  key: string;
  title: string;
  description: string;
  unit: DeviationUnit;
  rows: DeviationRow[];
}

interface MixPayload {
  startYear: number;
  endMonth: number;
  groupBy: 'customer' | 'project';
  customerView?: 'invoiceCustomer' | 'endcustomer';
  metric: MixMode;
  months: Array<keyof BusinessYearMonthValueMap>;
  rows: MixMatrixRow[];
}

interface HoursDetailRow {
  reporteddate: string;
  customername: string | null;
  endcustomer?: string | null;
  project_name: string | null;
  activity_name: string;
  hours: number;
}

const DUPLICATED_OCCUPANCY_EMPLOYEES = ['Patrik Nilsson', 'Johan Herbo', 'Håkan Wall'];

const HOURS_MODE_DEFINITIONS: Record<
  OccupancyMode,
  {
    label: string;
    field: keyof KpiMonthFacts;
    includes: string[];
    excludes: string[];
  }
> = {
  allHours: {
    label: 'Alla rapporterade timmar',
    field: 'all_hours',
    includes: ['alla verksamhetsgrupper'],
    excludes: [],
  },
  debitHours: {
    label: 'Debiterbara timmar',
    field: 'debit_hours',
    includes: ['Debiterbar tid'],
    excludes: ['alla andra grupper'],
  },
  sickHours: {
    label: 'Sjuk',
    field: 'sick_hours',
    includes: ['Sjuk'],
    excludes: ['alla andra grupper'],
  },
  leaveHours: {
    label: 'Tjänstledig',
    field: 'leave_hours',
    includes: ['Tjänstledigt'],
    excludes: ['alla andra grupper'],
  },
  vacationHours: {
    label: 'Semester',
    field: 'vacation_hours',
    includes: ['Semester'],
    excludes: ['alla andra grupper'],
  },
  compOutHours: {
    label: 'Komputtag',
    field: 'comp_out_hours',
    includes: ['Komputtag'],
    excludes: ['alla andra grupper'],
  },
  kitsconHours: {
    label: 'KitsCon',
    field: 'kitscon_hours',
    includes: ['Aktiviteter som heter KitsCon / KitsCon…'],
    excludes: ['övrig kompetensutveckling'],
  },
  competenceHours: {
    label: 'Kompetensutveckling',
    field: 'competence_hours',
    includes: ['int_komp utom KitsCon'],
    excludes: ['KitsCon', 'övriga grupper'],
  },
  internalProjectHours: {
    label: 'Interna projekt',
    field: 'internal_project_hours',
    includes: ['Interna projekt'],
    excludes: ['alla andra grupper'],
  },
  ohHours: {
    label: 'Chefstid / OH',
    field: 'oh_hours',
    includes: ['OH'],
    excludes: ['alla andra grupper'],
  },
  idleGroupHours: {
    label: 'Idle / utan uppdrag',
    field: 'idle_group_hours',
    includes: ['unit idle', 'aktiviteten "Utan uppdrag" under internt'],
    excludes: ['övrig intern tid'],
  },
  salesMarketingHours: {
    label: 'Sälj / marknad',
    field: 'sales_marketing_hours',
    includes: ['Sälj / kundmöte', 'Marknadsföring'],
    excludes: ['övrig intern tid'],
  },
  otherInternalHours: {
    label: 'Övrig intern tid',
    field: 'other_internal_hours',
    includes: ['kvarvarande intern tid i unit internt'],
    excludes: ['Idle / utan uppdrag', 'Sälj / marknad'],
  },
};

const DEVIATION_THRESHOLDS = {
  lowBudgetUtilization: 0.9,
  highNetUtilization: 0.95,
  highIdleRatio: 0.15,
  highInternalRatio: 0.25,
  highZeroPriceDebitHours: 8,
  highUninvoicedValue: 100000,
} as const;

const KPI_OPTION_GROUPS: Array<{ label: string; options: Array<{ value: KpiMode; label: string }> }> = [
  {
    label: 'Kapacitet',
    options: [
      { value: 'internalRatio', label: 'Internandel' },
      { value: 'absenceRatio', label: 'Frånvaroandel' },
      { value: 'idleRatio', label: 'Idle-andel' },
      { value: 'ohRatio', label: 'OH-andel' },
      { value: 'internalProjectRatio', label: 'Kompetens-/internprojektandel' },
    ],
  },
  {
    label: 'Värde',
    options: [
      { value: 'debitValue', label: 'Debiterbart värde' },
      { value: 'valuePerAvailableHour', label: 'Värde per tillgänglig timme' },
      { value: 'valuePerNetHour', label: 'Värde per närvarotimme' },
    ],
  },
  {
    label: 'Pipeline',
    options: [
      { value: 'invoicedHoursRatio', label: 'Faktureringsgrad timmar' },
      { value: 'invoicedValueRatio', label: 'Faktureringsgrad värde' },
      { value: 'uninvoicedHoursRatio', label: 'Ofaktureringsgrad timmar' },
      { value: 'uninvoicedValueRatio', label: 'Ofaktureringsgrad värde' },
      { value: 'zeroPriceDebitRatio', label: '0-pris andel av debittid' },
      { value: 'invoicedDebitHours', label: 'Fakturerade debiterbara timmar' },
      { value: 'invoicedDebitValue', label: 'Fakturerat debiterbart värde' },
      { value: 'uninvoicedDebitHours', label: 'Ofakturerade debiterbara timmar' },
      { value: 'uninvoicedDebitValue', label: 'Ofakturerat debiterbart värde' },
      { value: 'zeroPriceDebitHours', label: '0-pris debittid' },
    ],
  },
];

interface ReportHelpDefinition {
  title: string;
  description: string;
  numerator?: string;
  denominator?: string;
  includes: string[];
  excludes: string[];
  usefulFor: string;
}

const OCCUPANCY_REPORT_HELP: Record<Exclude<OccupancyReportType, 'kpi'>, ReportHelpDefinition> = {
  bonus: {
    title: 'Underlag för årsbonus',
    description:
      'Visar det underlag som idag används för att bedöma hur stor del av årsbonusen varje anställd ska få.',
    numerator: 'Alla rapporterade timmar utom tjänstledighet.',
    denominator: 'Tillgängliga arbetstimmar i den valda perioden.',
    includes: ['debit', 'idle', 'int_komp', 'internt', 'sjuk', 'semester', 'komp_ut', 'int_proj', 'oh'],
    excludes: ['tjänstledighet'],
    usefulFor: 'Jämförelse med nuvarande bonusmodell och uppföljning av bonusutfall över tid.',
  },
  utilization: {
    title: 'Beläggning',
    description:
      'Visar debiterbara timmar jämfört med vald kapacitetsgrund. Välj beläggningssätt för att se rå kapacitet, faktisk närvarokapacitet, budget eller gap mot budget.',
    numerator: 'Debiterbara timmar.',
    denominator: 'Beror på valt beläggningssätt.',
    includes: ['debiterbara timmar i täljaren', 'frånvaro eller budget i jämförelsen beroende på valt läge'],
    excludes: ['andra units än debit i täljaren'],
    usefulFor: 'VD-styrning av kapacitet, faktisk debitering och avvikelse mot plan.',
  },
  hours: {
    title: 'Timmar',
    description:
      'Visar faktiska rapporterade timmar per månad enligt verksamhetsgrupperna. Välj läge för att se alla rapporterade timmar eller en specifik grupp som debiterbara timmar, KitsCon, interna projekt eller chefstid.',
    includes: ['Beror på valt läge under Timmar.'],
    excludes: ['I varje läge visas bara den valda verksamhetsgruppen.'],
    usefulFor: 'Volymuppföljning i råa timmar i stället för procent eller värde.',
  },
  averageRate: {
    title: 'Verkligt timpris',
    description:
      'Visar verkligt, viktat timpris per månad och total för perioden. Periodtotalen räknas på hela perioden och är inte ett medel av månadsvärdena.',
    numerator: 'Debiterbart värde för debiterbar tid.',
    denominator: 'Debiterbara timmar.',
    includes: ['debit'],
    excludes: ['alla andra units'],
    usefulFor: 'Uppföljning av verkligt intjänat timpris per konsult och månad.',
  },
  zeroPriceDebit: {
    title: 'Debiterbar tid detalj',
    description:
      'Visar debiterbar tid per person och månad som aktivitets-sammanfattningar i varje cell. Filtrera på fakturerat/ofakturerat och prisnivå för att felsöka eller följa upp detaljer.',
    includes: ['alltid debit', 'därefter valt faktureringsstatusfilter', 'därefter valt prisfilter'],
    excludes: ['alla andra units'],
    usefulFor: 'Hitta debittid som saknar pris, har fel pris, inte är fakturerad eller behöver följas upp i detalj.',
  },
  zeroPriceProjects: {
    title: 'Projekt med 0-pris tid',
    description:
      'Visar vilka projekt som driver den valda kombinationen av debiterbar tid, faktureringsstatus och prisnivå över vald period.',
    includes: [
      'alltid debit',
      'grupperat per kund och projekt',
      'timmar, värde, antal personer, antal aktiviteter och första/sista datum',
    ],
    excludes: ['alla andra units'],
    usefulFor:
      'Bra för att snabbt hitta vilka projekt som driver 0-pris tid, ofakturerad debit eller annan vald debiterbar specialmängd.',
  },
  customerMix: {
    title: 'Kundmix',
    description:
      'Visar hur debiterbar volym eller debiterbart värde fördelas per kund och månad i perioden.',
    includes: ['alltid debit', 'grupperat per kund', 'månadsvis total för vald period'],
    excludes: ['alla andra units'],
    usefulFor: 'Bra för att förstå kundberoende, säsongsmönster och vilka kunder som bär timmar, värde eller timpris.',
  },
  projectMix: {
    title: 'Projektmix',
    description:
      'Visar hur debiterbar volym eller debiterbart värde fördelas per projekt och månad i perioden.',
    includes: ['alltid debit', 'grupperat per kund och projekt', 'månadsvis total för vald period'],
    excludes: ['alla andra units'],
    usefulFor: 'Bra för att se vilka projekt som bär verksamheten, vilka som växer och vilka som har svagare värdeutveckling.',
  },
  employeeCost: {
    title: 'Lönekostnad per anställd',
    description:
      'Visar månadskostnad per anställd för verksamhetsåret. Varje månad använder löneraden som gäller vid månadens slut.',
    numerator: 'salary + bonus + overtime + cbonus + responsability.',
    includes: ['anställda', 'affärsområde från employee.costcenter', 'gällande lönerad per månad'],
    excludes: ['underkonsulter när Visa underkonsulter inte är valt', 'sysselsättningsgrad multipliceras inte in'],
    usefulFor: 'Budget- och kostnadsuppföljning per affärsområde och månad.',
  },
  absenceHours: {
    title: 'Frånvarotimmar',
    description:
      'Visar frånvarotimmar per anställd och månad samt tillgängliga arbetstimmar och frånvaroandel.',
    numerator: 'Sjuk + ledig + semester, och komp ut om valet är aktiverat.',
    denominator: 'Tillgängliga arbetstimmar i månaden eller perioden.',
    includes: ['sjuk', 'ledig', 'semester', 'tillgängliga arbetstimmar'],
    excludes: ['komp ut när valet inte är aktiverat'],
    usefulFor: 'Uppföljning av frånvarotimmar utan att blanda ihop det med KPI-vyn för frånvaroandel.',
  },
  deviations: {
    title: 'Avvikelser',
    description:
      'Visar bara personer och månader där viktiga KPI:er avviker från definierade styrgränser.',
    includes: [
      'låg beläggning mot budget',
      'hög beläggning netto efter frånvaro',
      'hög idle-andel',
      'hög internandel',
      'hög 0-pris debittid',
      'högt ofakturerat värde',
    ],
    excludes: ['månader utan träff', 'personer utan avvikelse'],
    usefulFor: 'Snabb vd-översikt över vilka avvikelser som kräver åtgärd eller uppföljning.',
  },
};

const KPI_HELP: Record<KpiMode, ReportHelpDefinition> = {
  internalRatio: {
    title: 'KPI: Internandel',
    description: 'Visar hur stor del av den rapporterade tiden som går till internt arbete i stället för debitering.',
    numerator: 'Idle, kompetens, intern tid, internprojekt och OH.',
    denominator: 'Alla rapporterade timmar utom tjänstledighet.',
    includes: ['idle', 'int_komp', 'internt', 'int_proj', 'oh'],
    excludes: ['tjl från nämnaren', 'debit i täljaren'],
    usefulFor: 'Bra för att förstå hur mycket kapacitet som går till internt arbete i stället för kundarbete.',
  },
  absenceRatio: {
    title: 'KPI: Frånvaroandel',
    description: 'Visar hur stor del av tillgängliga arbetstimmar som försvinner till frånvaro.',
    numerator: 'Sjuk, semester och tjänstledighet.',
    denominator: 'Tillgängliga arbetstimmar.',
    includes: ['sjuk', 'semester', 'tjl'],
    excludes: ['övriga units i täljaren'],
    usefulFor: 'Bra för att förstå varför brutto- och nettobeläggning skiljer sig mellan månader.',
  },
  idleRatio: {
    title: 'KPI: Idle-andel',
    description: 'Visar hur stor del av den rapporterade tiden som ligger som idle.',
    numerator: 'Idle.',
    denominator: 'Alla rapporterade timmar utom tjänstledighet.',
    includes: ['idle'],
    excludes: ['alla andra units i täljaren'],
    usefulFor: 'Bra för att upptäcka perioder där kapacitet finns men inte används i kundarbete eller internt arbete.',
  },
  ohRatio: {
    title: 'KPI: OH-andel',
    description: 'Visar hur stor del av den rapporterade tiden som går till chefstid och overhead.',
    numerator: 'OH.',
    denominator: 'Alla rapporterade timmar utom tjänstledighet.',
    includes: ['oh'],
    excludes: ['alla andra units i täljaren'],
    usefulFor: 'Bra för att följa hur mycket lednings- och overheadtid som tas ut per person och månad.',
  },
  internalProjectRatio: {
    title: 'KPI: Kompetens-/internprojektandel',
    description: 'Visar hur stor del av den rapporterade tiden som går till kompetensutveckling och interna projekt.',
    numerator: 'Kompetens, intern tid och internprojekt.',
    denominator: 'Alla rapporterade timmar utom tjänstledighet.',
    includes: ['int_komp', 'internt', 'int_proj'],
    excludes: ['idle och oh ligger utanför täljaren'],
    usefulFor: 'Bra för att följa hur mycket tid som läggs på intern utveckling, kompetens och interna projekt.',
  },
  debitValue: {
    title: 'KPI: Debiterbart värde',
    description: 'Visar producerat debiterbart värde per månad och total för visad period.',
    numerator: 'Debiterbara timmar multiplicerat med pris, för rader där pris finns.',
    includes: ['debit med price > 0'],
    excludes: ['price = 0', 'alla andra units'],
    usefulFor: 'Bra när du vill se producerat ekonomiskt värde, inte bara timmar eller procent.',
  },
  valuePerAvailableHour: {
    title: 'KPI: Värde per tillgänglig timme',
    description: 'Visar hur mycket debiterbart värde som skapas per tillgänglig arbetstimme.',
    numerator: 'Debiterbart värde.',
    denominator: 'Tillgängliga arbetstimmar.',
    includes: ['debit med price > 0 i värdet'],
    excludes: ['price = 0 påverkar inte värdet'],
    usefulFor: 'Bra vd-mått när du vill kombinera prisnivå och kapacitetsutnyttjande.',
  },
  valuePerNetHour: {
    title: 'KPI: Värde per närvarotimme',
    description: 'Visar hur mycket debiterbart värde som skapas per faktisk närvarotimme.',
    numerator: 'Debiterbart värde.',
    denominator: 'Tillgängliga timmar minus semester, sjuk och tjänstledighet.',
    includes: ['debit med price > 0 i värdet'],
    excludes: ['frånvaro dras bort i nämnaren'],
    usefulFor: 'Bra när du vill förstå värdeproduktionen i faktisk närvarokapacitet.',
  },
  invoicedHoursRatio: {
    title: 'KPI: Faktureringsgrad timmar',
    description: 'Visar hur stor andel av debiterbara timmar som redan har fakturerats.',
    numerator: 'Fakturerade debiterbara timmar.',
    denominator: 'Alla debiterbara timmar.',
    includes: ['debit', 'statement_id IS NOT NULL i täljaren'],
    excludes: ['alla andra units', 'ej debiterbara timmar'],
    usefulFor: 'Bra för att följa hur snabbt levererad debittid omvandlas till fakturerad volym.',
  },
  invoicedValueRatio: {
    title: 'KPI: Faktureringsgrad värde',
    description: 'Visar hur stor andel av producerat debiterbart värde som redan har fakturerats.',
    numerator: 'Fakturerat debiterbart värde.',
    denominator: 'Allt debiterbart värde.',
    includes: ['debit med price > 0', 'statement_id IS NOT NULL i täljaren'],
    excludes: ['alla andra units', 'price = 0 påverkar inte värdenämnaren'],
    usefulFor: 'Bra för att följa hur stor andel av producerat värde som redan är realiserat i fakturering.',
  },
  uninvoicedHoursRatio: {
    title: 'KPI: Ofaktureringsgrad timmar',
    description: 'Visar hur stor andel av debiterbara timmar som ännu inte har fakturerats.',
    numerator: 'Ofakturerade debiterbara timmar.',
    denominator: 'Alla debiterbara timmar.',
    includes: ['debit', 'statement_id IS NULL i täljaren'],
    excludes: ['alla andra units', 'ej debiterbara timmar'],
    usefulFor: 'Bra för att se hur stor del av debittiden som fortfarande ligger kvar i faktureringspipen.',
  },
  uninvoicedValueRatio: {
    title: 'KPI: Ofaktureringsgrad värde',
    description: 'Visar hur stor andel av debiterbart värde som ännu inte har fakturerats.',
    numerator: 'Ofakturerat debiterbart värde.',
    denominator: 'Allt debiterbart värde.',
    includes: ['debit med price > 0', 'statement_id IS NULL i täljaren'],
    excludes: ['alla andra units', 'price = 0 påverkar inte värdenämnaren'],
    usefulFor: 'Bra för att se hur stor del av producerat värde som ännu inte är realiserat i fakturering.',
  },
  zeroPriceDebitRatio: {
    title: 'KPI: 0-pris andel av debittid',
    description: 'Visar hur stor andel av debittiden som har pris 0.',
    numerator: '0-pris debittid.',
    denominator: 'Alla debiterbara timmar.',
    includes: ['debit med price = 0 i täljaren'],
    excludes: ['alla andra units'],
    usefulFor: 'Bra som snabb kvalitetsindikator för prissättningen av debiterbar tid.',
  },
  invoicedDebitHours: {
    title: 'KPI: Fakturerade debiterbara timmar',
    description: 'Visar debiterbara timmar som redan har fakturerats.',
    numerator: 'Debiterbara timmar där faktura finns.',
    includes: ['debit med statement_id IS NOT NULL'],
    excludes: ['ofakturerade debit-rader', 'alla andra units'],
    usefulFor: 'Bra för att se hur mycket levererad debittid som redan gått hela vägen till fakturering.',
  },
  invoicedDebitValue: {
    title: 'KPI: Fakturerat debiterbart värde',
    description: 'Visar debiterbart värde som redan har fakturerats.',
    numerator: 'Debiterbart värde för rader där faktura finns.',
    includes: ['debit med price > 0 och statement_id IS NOT NULL'],
    excludes: ['ofakturerade debit-rader', 'price = 0'],
    usefulFor: 'Bra för att följa realiserat debiterbart värde per månad och total för perioden.',
  },
  uninvoicedDebitHours: {
    title: 'KPI: Ofakturerade debiterbara timmar',
    description: 'Visar debiterbara timmar som ännu inte har fakturerats.',
    numerator: 'Debiterbara timmar där faktura saknas.',
    includes: ['debit med statement_id IS NULL'],
    excludes: ['fakturerade debit-rader', 'alla andra units'],
    usefulFor: 'Bra för att fånga faktureringssläp och ofakturerad leveranskapacitet.',
  },
  uninvoicedDebitValue: {
    title: 'KPI: Ofakturerat debiterbart värde',
    description: 'Visar debiterbart värde som ännu inte har fakturerats.',
    numerator: 'Debiterbart värde för rader där faktura saknas.',
    includes: ['debit med price > 0 och statement_id IS NULL'],
    excludes: ['fakturerade debit-rader', 'price = 0'],
    usefulFor: 'Bra för att se vilka pengar som finns i leveransen men ännu inte realiserats i fakturering.',
  },
  zeroPriceDebitHours: {
    title: 'KPI: 0-pris debittid',
    description: 'Visar debiterbara timmar som har pris 0.',
    numerator: 'Debiterbara timmar där pris är 0.',
    includes: ['debit med price = 0'],
    excludes: ['debit med price > 0', 'alla andra units'],
    usefulFor: 'Bra för att hitta felprissättning, läckage och aktiviteter som bör granskas.',
  },
};

@Component({
  selector: 'app-ng-test',
  template: require('./ng-test.component.html').default,
  styles: [require('!!raw-loader!sass-loader!./ng-test.component.scss').default],
  standalone: false,
})
export class NgTestComponent {
  selectedDate!: string;
  selectedDate2: string = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format

  // Variables for IO components
  ioBsDate: moment.Moment = moment();
  ioFlatpickrDate: string = new Date().toISOString().split('T')[0];

  // Progress bar demo data
  progressBar1 = {
    fardig1: 60, // Green segment (60%)
    fardig2: 25, // Yellow segment (25%)
    fardig3: 15, // Red segment (15%)
    total1: 24, // Hours worked
    total2: 10, // Hours overtime
    total3: 6, // Hours flexible
    workHours: 40, // Total work hours
  };

  monthTotal: any = {};
  dialogResult = 'Ingen dialog körd ännu';

  // Test variables for manual progress bar testing
  testRapporterat: number = 184;
  testTotal: number = 184;
  occupancyStartYear = 2025;
  occupancyEndMonth = 6;
  occupancyIncludeContractors = false;
  occupancyCostcentersSelected: string[] = [];
  occupancyCostcenters: Costcenter[] = [];
  occupancyBonusCapAtHundred = false;
  occupancyAverageRateIncludeZero = false;
  occupancyDuplicateSelectedEmployees = false;
  absenceSelectedTypes: string[] = ['sick', 'vab', 'leave', 'vacation'];
  occupancyReportType: OccupancyReportType = 'bonus';
  occupancyModes: OccupancyMode[] = ['allHours'];
  utilizationMode: UtilizationMode = 'gross';
  occupancyUtilizationFillMissingAsLeave = false;
  mixMode: MixMode = 'value';
  customerMixView: CustomerMixView = 'invoiceCustomer';
  mixSortKey: MixSortKey = 'customer';
  mixSortDirection: MixSortDirection = 'asc';
  kpiMode: KpiMode = 'internalRatio';
  occupancyMonths: MonthWorkHours[] = [];
  occupancyEndMonthOptions: MonthWorkHours[] = [];
  occupancyRows: OccupancyRow[] = [];
  occupancyZeroPriceRows: ZeroPriceMatrixRow[] = [];
  occupancyProjectSummaryRows: DebitProjectSummaryRow[] = [];
  occupancyMixRows: MixMatrixRow[] = [];
  employeeCostRows: EmployeeCostRow[] = [];
  absenceRows: AbsenceRow[] = [];
  absenceTotals: AbsenceFacts | null = null;
  occupancyDeviationSections: DeviationSection[] = [];
  occupancyHoursBreakdownCheck: HoursBreakdownCheckSummary | null = null;
  occupancyLoading = false;
  occupancyError = '';
  occupancyInfoOpen = false;
  occupancySectionTitle = '';
  occupancyLoadingLabel = '';
  occupancyEmptyState = '';
  currentOccupancyReportHelp!: ReportHelpDefinition;
  debitDetailInvoiceState: DebitDetailInvoiceState = 'uninvoiced';
  debitDetailPriceMode: DebitDetailPriceMode = 'zeroOrNull';
  budgetStartYear = 2025;
  budgetMonths: MonthWorkHours[] = [];
  ownBudgetInputs: BillableBudgetInputs = {
    semester: 240,
    kitscon: 16,
    kompetensutveckling: 56,
    månadsmöten: 8,
    genomsnittSjuk: 16,
  };
  contractorBudgetInputs: BillableBudgetInputs = {
    semester: 240,
    kitscon: 0,
    kompetensutveckling: 16,
    månadsmöten: 0,
    genomsnittSjuk: 16,
  };
  budgetCalculation: BillableBudgetCalculation | null = null;
  budgetInputFields: Array<{ label: string; key: BudgetInputKey }> = [
    { label: 'Semester', key: 'semester' },
    { label: 'Kitscon', key: 'kitscon' },
    { label: 'Kompetensutveckling', key: 'kompetensutveckling' },
    { label: 'Månadsmöten', key: 'månadsmöten' },
    { label: 'Genomsnitt sjuk', key: 'genomsnittSjuk' },
  ];
  readonly kpiOptionGroups = KPI_OPTION_GROUPS;
  readonly hoursModeOptions = Object.entries(HOURS_MODE_DEFINITIONS).map(([value, config]) => ({
    value: value as OccupancyMode,
    label: config.label,
  }));

  private destroyRef = inject(DestroyRef);

  // Mock data for project selection
  mockProjects: Project[] = [
    {
      id: 1,
      name: 'Project Alpha',
      activities: [
        { id: 1, name: 'Development' },
        { id: 2, name: 'Testing' },
        { id: 3, name: 'Documentation' },
      ],
    },
    {
      id: 2,
      name: 'Project Beta',
      activities: [
        { id: 4, name: 'Design' },
        { id: 5, name: 'Implementation' },
        { id: 6, name: 'Code Review' },
      ],
    },
    {
      id: 3,
      name: 'Project Gamma',
      activities: [
        { id: 7, name: 'Planning' },
        { id: 8, name: 'Execution' },
        { id: 9, name: 'Monitoring' },
      ],
    },
  ];

  constructor(
    @Inject(HttpClient) private http: HttpClient,
    @Inject(TimerecordsService) private timerecordsService: TimerecordsService,
    @Inject(DialogService) private dialogService: DialogService,
    @Inject(ModalService) private modalService: ModalService,
    @Inject(DateHolidayService) private dateHolidayService: DateHolidayService,
    @Inject(FileSaverService) private fileSaverService: FileSaverService
  ) {
    console.log('NgTestComponent initialized');
    console.log('TimerecordsService:', this.timerecordsService); // Debug log
  }

  async openProjectModal(): Promise<void> {
    const result = (await this.modalService.show(ProjectSelectionModalComponent, {
      headerText: 'Välj Projekt och Aktivitet',
      closeButtonText: 'Avbryt',
      actionButtonText: 'Spara',
      projects: this.mockProjects,
    })) as ProjectSelectionDialogResult | undefined;

    if (!result) {
      console.log('Modal closed without saving');
      return;
    }

    console.log('Project selected:', result.project);
    console.log('Activity selected:', result.activity);
    alert(`Valt: ${result.project.name} - ${result.activity.name}`);
  }

  async testDialogAlert(): Promise<void> {
    await this.dialogService.alert({
      title: 'DialogService alert',
      body: 'Detta är en testdialog från den nya DialogService.',
      buttonText: 'Stäng',
    });

    this.dialogResult = 'Alert stängdes';
  }

  async testDialogConfirm(): Promise<void> {
    const confirmed = await this.dialogService.confirm({
      title: 'DialogService confirm',
      body: 'Bekräfta att den nya DialogService fungerar.',
      confirmText: 'Bekräfta',
      cancelText: 'Avbryt',
    });

    this.dialogResult = confirmed ? 'Confirm: bekräftad' : 'Confirm: avbruten';
  }

  /**
   * Test progress bar with manual values (no API call)
   */
  testProgressBar(): void {
    // Initialize with 0% for animation effect
    this.monthTotal = {
      month: 'Test',
      rapporterat: 0,
      total: this.testTotal,
      fardig: 0,
    };

    const finalPercentage = (this.testRapporterat / this.testTotal) * 100;

    // Update to actual values for animation
    setTimeout(() => {
      this.monthTotal = {
        month: 'Test',
        rapporterat: this.testRapporterat,
        total: this.testTotal,
        fardig: finalPercentage,
      };
    }, 50);
  }

  testLoadingBar() {
    console.log('Testing loading bar...');
    console.log('Making HTTP request to JSONPlaceholder API...');

    // Add a delay parameter to make the request slower and loading bar more visible
    this.http.get('https://jsonplaceholder.typicode.com/posts/1?delay=2000').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        console.log('API response received:', data);
      },
      error: (error) => {
        console.error('API error:', error);
      },
      complete: () => {
        console.log('API request completed');
      },
    });
  }

  /**
   * Update statistics for the current month
   * @param from Date string in YYYY-MM-DD format (start of the month)
   * @param to Date string in YYYY-MM-DD format (end of the month)
   * @param workHours Total expected work hours for the month
   */
  updateStats(from: string, to: string, workHours: number): void {
    const momentDate = moment(from);

    // Create period from the from date
    const period = {
      from: moment(from),
      to: moment(to),
    };

    // Get the month name from the from parameter
    const month = momentDate.format('MMMM');

    // Initialize with 0% for animation effect
    this.monthTotal = {
      month: month,
      rapporterat: 0,
      total: workHours,
      fardig: 0,
    };

    // Call the service to get time records for the period
    this.timerecordsService.getWeek(period.from, period.to).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        let sum = 0;

        // Calculate the total reported time
        for (let i = 0; i < data.length; i++) {
          sum += data[i].time;
        }

        const finalPercentage = (sum / workHours) * 100;

        this.monthTotal = {
          month: month,
          rapporterat: sum,
          total: workHours,
          fardig: finalPercentage,
        };

        console.log('Month total:', this.monthTotal);
      },
      error: (error) => {
        console.error('Error fetching time records:', error);
      },
    });
  }

  ngOnInit() {
    this.refreshOccupancyPresentationState();
    this.loadOccupancyCostcenters();
    this.loadOccupancyReport();
    this.calculateBudgetReport();
  }

  loadOccupancyCostcenters(): void {
    this.http
      .get<Costcenter[]>('/api/costcenters')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (rows) => {
          this.occupancyCostcenters = rows;
        },
        error: (error) => {
          console.error('Error loading costcenters for occupancy report:', error);
        },
      });
  }

  loadOccupancyReport(): void {
    this.occupancyMonths = this.getVisibleOccupancyMonths();
    this.refreshOccupancyPresentationState();
    this.occupancyLoading = true;
    this.occupancyError = '';
    this.occupancyZeroPriceRows = [];
    this.occupancyProjectSummaryRows = [];
    this.occupancyMixRows = [];
    this.employeeCostRows = [];
    this.absenceRows = [];
    this.absenceTotals = null;
    this.occupancyDeviationSections = [];

    let params = new HttpParams();
    params = params.set(
      'includeContractors',
      String(this.shouldIncludeContractorsForCurrentReport())
    );
    const costcenterParam = this.getOccupancyCostcenterParam();
    if (this.shouldUseCostcenterFilterForCurrentReport() && costcenterParam) {
      params = params.set('costcenter', costcenterParam);
    }

    let request$;
    if (this.occupancyReportType === 'bonus') {
      params = params.set('includeContractors', 'false');
      params = params.set('endMonth', String(this.occupancyEndMonth));
      params = params.set('family', 'capacity');
      request$ = this.http.get<KpiMetricsPayload>(`/api/kpi/metrics/business-year/${this.occupancyStartYear}`, {
        params,
      });
    } else if (this.occupancyReportType === 'utilization') {
      params = params.set('family', 'capacity');
      params = params.set('endMonth', String(this.occupancyEndMonth));
      if (this.utilizationMode === 'netAfterAbsence' && this.occupancyUtilizationFillMissingAsLeave) {
        params = params.set('fillMissingAsLeave', 'true');
      }
      request$ = this.http.get<KpiMetricsPayload>(`/api/kpi/metrics/business-year/${this.occupancyStartYear}`, {
        params,
      });
    } else if (this.occupancyReportType === 'kpi') {
      params = params.set('endMonth', String(this.occupancyEndMonth));
      params = params.set('family', this.getKpiFamily());
      request$ = this.http.get<KpiMetricsPayload>(`/api/kpi/metrics/business-year/${this.occupancyStartYear}`, {
        params,
      });
    } else if (this.occupancyReportType === 'deviations') {
      params = params.set('endMonth', String(this.occupancyEndMonth));
      params = params.set('family', 'all');
      request$ = this.http.get<KpiMetricsPayload>(`/api/kpi/metrics/business-year/${this.occupancyStartYear}`, {
        params,
      });
    } else if (this.occupancyReportType === 'averageRate') {
      params = params.set('endMonth', String(this.occupancyEndMonth));
      request$ = this.http.get<KpiFactsPayload>(
        `/api/kpi/facts/business-year/${this.occupancyStartYear}`,
        { params }
      );
    } else if (this.occupancyReportType === 'zeroPriceDebit') {
      const from = `${this.occupancyStartYear}-07-01`;
      const to = this.getOccupancyEndDate();
      params = params.set('invoiceState', this.debitDetailInvoiceState);
      params = params.set('priceMode', this.debitDetailPriceMode);
      request$ = this.http.get<UninvoicedZeroPriceDebitRow[]>(
        `/api/kpi/drilldown/debit-detail/${from}/${to}`,
        { params }
      );
    } else if (this.occupancyReportType === 'zeroPriceProjects') {
      const from = `${this.occupancyStartYear}-07-01`;
      const to = this.getOccupancyEndDate();
      params = params.set('invoiceState', this.debitDetailInvoiceState);
      params = params.set('priceMode', this.debitDetailPriceMode);
      request$ = this.http.get<DebitProjectSummaryRow[]>(
        `/api/kpi/drilldown/debit-project-summary/${from}/${to}`,
        { params }
      );
    } else if (this.occupancyReportType === 'customerMix' || this.occupancyReportType === 'projectMix') {
      params = params.set('endMonth', String(this.occupancyEndMonth));
      params = params.set('groupBy', this.occupancyReportType === 'projectMix' ? 'project' : 'customer');
      params = params.set('metric', this.mixMode);
      if (this.occupancyReportType === 'customerMix') {
        params = params.set('customerView', this.customerMixView);
      }
      request$ = this.http.get<MixPayload>(
        `/api/kpi/mix/business-year/${this.occupancyStartYear}`,
        { params }
      );
    } else if (this.occupancyReportType === 'employeeCost') {
      params = params.set('endMonth', String(this.occupancyEndMonth));
      request$ = this.http.get<EmployeeCostPayload>(
        `/api/kpi/employee-costs/business-year/${this.occupancyStartYear}`,
        { params }
      );
    } else if (this.occupancyReportType === 'absenceHours') {
      params = params.set('endMonth', String(this.occupancyEndMonth));
      params = params.set('types', this.getAbsenceTypesParam());
      request$ = this.http.get<AbsencePayload>(
        `/api/kpi/absence/business-year/${this.occupancyStartYear}`,
        { params }
      );
    } else {
      params = params.set('endMonth', String(this.occupancyEndMonth));
      request$ = this.http.get<KpiFactsPayload>(`/api/kpi/facts/business-year/${this.occupancyStartYear}`, {
        params,
      });
    }

    request$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (rows) => {
          if (this.occupancyReportType === 'zeroPriceDebit') {
            this.occupancyRows = [];
            this.occupancyProjectSummaryRows = [];
            this.occupancyZeroPriceRows = this.expandZeroPriceMatrixRows(
              this.mapZeroPriceRowsToMatrix(rows as UninvoicedZeroPriceDebitRow[])
            );
            this.occupancyHoursBreakdownCheck = null;
          } else if (this.occupancyReportType === 'zeroPriceProjects') {
            this.occupancyRows = [];
            this.occupancyZeroPriceRows = [];
            this.occupancyProjectSummaryRows = rows as DebitProjectSummaryRow[];
            this.occupancyMixRows = [];
            this.occupancyHoursBreakdownCheck = null;
          } else if (this.occupancyReportType === 'customerMix' || this.occupancyReportType === 'projectMix') {
            this.occupancyRows = [];
            this.occupancyZeroPriceRows = [];
            this.occupancyProjectSummaryRows = [];
            this.occupancyMixRows = (rows as MixPayload).rows;
            this.employeeCostRows = [];
            this.occupancyHoursBreakdownCheck = null;
          } else if (this.occupancyReportType === 'employeeCost') {
            this.occupancyRows = [];
            this.occupancyZeroPriceRows = [];
            this.occupancyProjectSummaryRows = [];
            this.occupancyMixRows = [];
            this.employeeCostRows = (rows as EmployeeCostPayload).rows;
            this.absenceRows = [];
            this.absenceTotals = null;
            this.occupancyHoursBreakdownCheck = null;
          } else if (this.occupancyReportType === 'absenceHours') {
            const payload = rows as AbsencePayload;
            this.occupancyRows = [];
            this.occupancyZeroPriceRows = [];
            this.occupancyProjectSummaryRows = [];
            this.occupancyMixRows = [];
            this.employeeCostRows = [];
            this.absenceRows = this.expandAbsenceRows(payload.rows);
            this.absenceTotals = payload.totals;
            this.occupancyHoursBreakdownCheck = null;
          } else if (this.occupancyReportType === 'deviations') {
            this.occupancyRows = [];
            this.occupancyZeroPriceRows = [];
            this.occupancyProjectSummaryRows = [];
            this.occupancyMixRows = [];
            this.employeeCostRows = [];
            this.absenceRows = [];
            this.absenceTotals = null;
            this.occupancyDeviationSections = this.buildDeviationSections(rows as KpiMetricsPayload);
            this.occupancyHoursBreakdownCheck = null;
          } else if (this.occupancyReportType === 'utilization') {
            this.occupancyZeroPriceRows = [];
            this.occupancyProjectSummaryRows = [];
            this.occupancyMixRows = [];
            this.employeeCostRows = [];
            this.absenceRows = [];
            this.absenceTotals = null;
            this.occupancyRows = this.expandOccupancyRows(
              this.mapMetricsRowsToOccupancyRows(rows as KpiMetricsPayload, (month, totals) => {
                switch (this.utilizationMode) {
                  case 'netAfterAbsence':
                    return { month: month.utilization_net_absence ?? null, total: totals.utilization_net_absence ?? null };
                  case 'againstBudget':
                    return { month: month.utilization_budget ?? null, total: totals.utilization_budget ?? null };
                  case 'budgetGapHours':
                    return { month: month.budget_gap_hours ?? null, total: totals.budget_gap_hours ?? null };
                  default:
                    return { month: month.utilization_gross ?? null, total: totals.utilization_gross ?? null };
                }
              })
            );
            this.occupancyHoursBreakdownCheck = null;
          } else if (this.occupancyReportType === 'bonus') {
            this.occupancyZeroPriceRows = [];
            this.occupancyProjectSummaryRows = [];
            this.occupancyMixRows = [];
            this.employeeCostRows = [];
            this.absenceRows = [];
            this.absenceTotals = null;
            this.occupancyRows = this.expandOccupancyRows(
              this.mapMetricsRowsToOccupancyRows(rows as KpiMetricsPayload, (month, totals) => ({
                month: month.bonus_ratio ?? null,
                total: totals.bonus_ratio ?? null,
              }))
            );
            this.occupancyHoursBreakdownCheck = null;
          } else if (this.occupancyReportType === 'kpi') {
            this.occupancyZeroPriceRows = [];
            this.occupancyProjectSummaryRows = [];
            this.occupancyMixRows = [];
            this.employeeCostRows = [];
            this.absenceRows = [];
            this.absenceTotals = null;
            this.occupancyRows = this.expandOccupancyRows(
              this.mapMetricsRowsToOccupancyRows(rows as KpiMetricsPayload, (month, totals) => ({
                month: this.getKpiMetricValue(month),
                total: this.getKpiMetricValue(totals),
              }))
            );
            this.occupancyHoursBreakdownCheck = null;
          } else if (this.occupancyReportType === 'averageRate') {
            this.occupancyZeroPriceRows = [];
            this.occupancyProjectSummaryRows = [];
            this.occupancyMixRows = [];
            this.employeeCostRows = [];
            this.absenceRows = [];
            this.absenceTotals = null;
            this.occupancyRows = this.expandOccupancyRows(
              this.mapFactsRowsToOccupancyRows(rows as KpiFactsPayload, (month, totals) => {
                const monthValue = this.occupancyAverageRateIncludeZero
                  ? ((month.debit_hours ?? 0) > 0 ? (month.debit_value ?? 0) / (month.debit_hours ?? 0) : null)
                  : (month.avg_hour_rate ?? null);
                const totalValue = this.occupancyAverageRateIncludeZero
                  ? ((totals.debit_hours ?? 0) > 0 ? (totals.debit_value ?? 0) / (totals.debit_hours ?? 0) : null)
                  : (totals.avg_hour_rate ?? null);
                return {
                  month: monthValue,
                  total: totalValue,
                };
              })
            );
            this.occupancyHoursBreakdownCheck = null;
          } else {
            const factsPayload = rows as KpiFactsPayload;
            this.occupancyZeroPriceRows = [];
            this.occupancyProjectSummaryRows = [];
            this.occupancyMixRows = [];
            this.employeeCostRows = [];
            this.absenceRows = [];
            this.absenceTotals = null;
            this.occupancyRows = this.expandOccupancyRows(
              this.mapFactsRowsToOccupancyRows(factsPayload, (month, totals) => ({
                month: this.getSelectedHoursValue(month),
                total: this.getSelectedHoursValue(totals),
              }))
            );
            this.occupancyHoursBreakdownCheck = this.buildHoursBreakdownCheckSummary(factsPayload);
          }
          this.occupancyLoading = false;
        },
        error: (error) => {
          this.occupancyRows = [];
          this.occupancyZeroPriceRows = [];
          this.occupancyProjectSummaryRows = [];
          this.occupancyMixRows = [];
          this.employeeCostRows = [];
          this.absenceRows = [];
          this.absenceTotals = null;
          this.occupancyDeviationSections = [];
          this.occupancyHoursBreakdownCheck = null;
          this.occupancyLoading = false;
          this.occupancyError = error?.error?.error || 'Kunde inte ladda beläggningsrapporten.';
          console.error('Error loading occupancy report:', error);
        },
      });
  }

  getOccupancyValue(row: OccupancyRow, month: MonthWorkHours): number | null {
    const key = this.getMonthKey(month.month);
    return this.normalizeOccupancyValue(row[key]);
  }

  getEmployeeCostValue(row: EmployeeCostRow, month: MonthWorkHours): number | null {
    const key = this.getBusinessYearMonthKey(month.month);
    return row.months[key] ?? null;
  }

  getEmployeeCostSalaryMonth(row: EmployeeCostRow, month: MonthWorkHours): EmployeeCostSalaryMonth | null {
    const key = this.getBusinessYearMonthKey(month.month);
    return row.salary_months[key] ?? null;
  }

  getAbsenceMonth(row: AbsenceRow, month: MonthWorkHours): AbsenceFacts | null {
    const key = this.getBusinessYearMonthKey(month.month);
    return row.months[key] ?? null;
  }

  getAbsenceMonthValue(row: AbsenceRow, month: MonthWorkHours): number | null {
    return this.getAbsenceMonth(row, month)?.absence_hours ?? null;
  }

  getAbsenceTypesParam(): string {
    return this.absenceSelectedTypes.length > 0
      ? this.absenceSelectedTypes.join(',')
      : 'none';
  }

  isAbsenceTypeSelected(type: string): boolean {
    return this.absenceSelectedTypes.includes(type);
  }

  toggleAbsenceType(type: string, selected: boolean): void {
    const next = new Set(this.absenceSelectedTypes);
    if (selected) {
      next.add(type);
    } else {
      next.delete(type);
    }
    this.absenceSelectedTypes = ['sick', 'vab', 'leave', 'vacation'].filter((item) => next.has(item));
    this.loadOccupancyReport();
  }

  getAbsenceSelectionLabel(): string {
    const labels: Record<string, string> = {
      sick: 'Sjuk',
      vab: 'VAB/NAR',
      leave: 'Ledighet',
      vacation: 'Semester',
    };
    return this.absenceSelectedTypes.map((type) => labels[type]).filter(Boolean).join(', ') || 'Inga';
  }

  formatRatio(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return '';
    }
    return `${new Intl.NumberFormat('sv-SE', {
      useGrouping: false,
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    }).format(value * 100)} %`;
  }

  sumAbsenceMonth(month: MonthWorkHours): number {
    return this.absenceRows.reduce((sum, row) => sum + Number(this.getAbsenceMonthValue(row, month) || 0), 0);
  }

  getHoursBreakdownCheckLabel(): string {
    if (!this.occupancyHoursBreakdownCheck) {
      return '';
    }

    if (this.occupancyHoursBreakdownCheck.ok) {
      return `Kontroll gruppering: OK. ${this.occupancyHoursBreakdownCheck.checkedEntries} kontroller matchar totalsumman.`;
    }

    return `Kontroll gruppering: avvikelse i ${this.occupancyHoursBreakdownCheck.mismatchEntries} av ${this.occupancyHoursBreakdownCheck.checkedEntries} kontroller. Max diff ${this.formatOccupancyValue(this.occupancyHoursBreakdownCheck.maxDifferenceHours)} h.`;
  }

  private buildHoursBreakdownCheckSummary(payload: KpiFactsPayload): HoursBreakdownCheckSummary {
    let checkedEntries = 0;
    let mismatchEntries = 0;
    let maxDifferenceHours = 0;
    let totalDifferenceHours = 0;

    payload.rows.forEach((row) => {
      payload.months.forEach((monthKey) => {
        const month = row.months[monthKey];
        if (!month?.active_in_month) {
          return;
        }
        checkedEntries += 1;
        const difference = Math.abs(Number(month.breakdown_hours_difference || 0));
        totalDifferenceHours += difference;
        maxDifferenceHours = Math.max(maxDifferenceHours, difference);
        if (!(month.breakdown_hours_ok ?? true)) {
          mismatchEntries += 1;
        }
      });

      checkedEntries += 1;
      const totalDifference = Math.abs(Number(row.totals.breakdown_hours_difference || 0));
      totalDifferenceHours += totalDifference;
      maxDifferenceHours = Math.max(maxDifferenceHours, totalDifference);
      if (!(row.totals.breakdown_hours_ok ?? true)) {
        mismatchEntries += 1;
      }
    });

    return {
      checkedEntries,
      mismatchEntries,
      maxDifferenceHours,
      totalDifferenceHours,
      ok: mismatchEntries === 0,
    };
  }

  private normalizeOccupancyValue(value: number | null | undefined): number | null {
    if (value === null || value === undefined) {
      return null;
    }

    if (this.occupancyReportType === 'bonus' && this.occupancyBonusCapAtHundred) {
      return Math.min(value, 1);
    }

    return value;
  }

  isOccupancyMonthInactive(
    row: Pick<OccupancyRow, 'start_date' | 'stop_date'>,
    month: MonthWorkHours
  ): boolean {
    if (!row.start_date) {
      return false;
    }

    const year = month.month >= 7 ? this.occupancyStartYear : this.occupancyStartYear + 1;
    const monthStart = moment({ year, month: month.month - 1, day: 1 }).startOf('month');
    const monthEndExclusive = moment(monthStart).add(1, 'month');
    const employeeStart = moment(row.start_date, 'YYYY-MM-DD');
    const employeeStop = row.stop_date ? moment(row.stop_date, 'YYYY-MM-DD') : null;

    return !employeeStart.isBefore(monthEndExclusive) || (employeeStop !== null && employeeStop.isBefore(monthStart));
  }

  formatOccupancyValue(value: number | null): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (this.isCurrentOccupancyPercentage()) {
      return `${new Intl.NumberFormat('sv-SE', {
        useGrouping: false,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value * 100)} %`;
    }

    if (this.occupancyReportType === 'averageRate') {
      return new Intl.NumberFormat('sv-SE', {
        useGrouping: false,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(value);
    }

    if (this.occupancyReportType === 'kpi') {
      return new Intl.NumberFormat('sv-SE', {
        useGrouping: false,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(value);
    }

    if (this.occupancyReportType === 'zeroPriceDebit') {
      return new Intl.NumberFormat('sv-SE', {
        useGrouping: false,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(value);
    }

    if (this.occupancyReportType === 'hours') {
      return new Intl.NumberFormat('sv-SE', {
        useGrouping: false,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(value);
    }

    return new Intl.NumberFormat('sv-SE', {
      useGrouping: false,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  }

  onOccupancyReportTypeChange(): void {
    if (this.occupancyReportType === 'hours') {
      this.occupancyModes = ['allHours'];
    }
    this.refreshOccupancyPresentationState();
    this.loadOccupancyReport();
  }

  onHoursModesChange(): void {
    const selected = Array.isArray(this.occupancyModes) ? this.occupancyModes : [];

    if (selected.length === 0) {
      this.occupancyModes = ['allHours'];
    } else if (selected.includes('allHours') && selected.length > 1) {
      this.occupancyModes = selected.filter((mode) => mode !== 'allHours');
    }

    this.refreshOccupancyPresentationState();
    this.loadOccupancyReport();
  }

  async openHoursDrilldown(row: OccupancyRow, month?: MonthWorkHours): Promise<void> {
    if (this.occupancyReportType !== 'hours') {
      return;
    }

    const isTotal = !month;
    const value = isTotal ? (row.total ?? 0) : (this.getOccupancyValue(row, month) ?? 0);
    if (value <= 0 || (!isTotal && month && this.isOccupancyMonthInactive(row, month))) {
      return;
    }

    const range = isTotal ? this.getVisiblePeriodDateRange() : this.getMonthDateRange(month!);
    const params = new HttpParams()
      .set('includeContractors', String(this.shouldIncludeContractorsForCurrentReport()))
      .set('groups', this.getSelectedHoursModes().join(','));

    const rows = await this.http
      .get<HoursDetailRow[]>(`/api/kpi/drilldown/hours-detail/${row.employee_id}/${range.from}/${range.to}`, {
        params,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .toPromise();

    await this.modalService.show(HoursDrilldownModalComponent, {
      title: `${this.getHoursModeSummaryLabel()}: ${row.name}${isTotal ? ` (${this.getVisiblePeriodLabel()})` : ` (${month!.monthName})`}`,
      rows: (rows || []) as HoursDrilldownRow[],
    });
  }

  toggleOccupancyInfo(): void {
    this.occupancyInfoOpen = !this.occupancyInfoOpen;
  }

  getCurrentOccupancyReportHelp(): ReportHelpDefinition {
    if (this.occupancyReportType === 'kpi') {
      return KPI_HELP[this.kpiMode];
    }

    const base = OCCUPANCY_REPORT_HELP[this.occupancyReportType];

    if (this.occupancyReportType === 'utilization') {
      const utilizationHelpMap: Record<UtilizationMode, Partial<ReportHelpDefinition>> = {
        gross: {
          title: 'Beläggning: Mot arbetstid',
          denominator: 'Tillgängliga arbetstimmar i månaden.',
          includes: ['debit i täljaren'],
          excludes: ['semester, sjuk och tjänstledigt dras inte bort i nämnaren'],
          usefulFor: 'Rå kapacitetsutnyttjande mot kalendern.',
        },
        netAfterAbsence: {
          title: 'Beläggning: Mot arbetstid efter frånvaro',
          denominator: 'Tillgängliga arbetstimmar minus semester, tjänstledigt och sjuk i månaden.',
          includes: [
            'debit i täljaren',
            'semester, tjänstledigt och sjuk i nämnaren som avdrag',
            ...(this.occupancyUtilizationFillMissingAsLeave
              ? ['saknade timmar fylls ut som tjänstledigt i rapportberäkningen']
              : []),
          ],
          excludes: ['annan intern tid påverkar inte nämnaren'],
          usefulFor: 'Rättvisare bild av debitering mot faktisk närvarokapacitet.',
        },
        againstBudget: {
          title: 'Beläggning: Mot budgeterad tid',
          denominator: 'Budgeterat fakturerbart per månad från Budgeterat Fakturerbart.',
          includes: ['debit i täljaren', 'budgeterad fakturerbar kapacitet i nämnaren'],
          excludes: ['frånvaro räknas inte direkt här utan via budgetmodellen'],
          usefulFor: 'Styrning mot plan per månad och år.',
        },
        budgetGapHours: {
          title: 'Beläggning: Gap mot budget',
          description: 'Visar debiterbara timmar minus budgeterat fakturerbart per månad och total för året.',
          numerator: 'Debiterbara timmar.',
          denominator: 'Ingen procentnämnare. Resultatet visas som timmar över eller under budget.',
          includes: ['debit i täljaren', 'budgeterad fakturerbar kapacitet som jämförelse'],
          excludes: ['inga andra units i täljaren'],
          usefulFor: 'Konkreta avvikelser i timmar mot plan.',
        },
      };

      return {
        ...base,
        ...utilizationHelpMap[this.utilizationMode],
      };
    }

    if (this.occupancyReportType === 'averageRate') {
      if (this.occupancyAverageRateIncludeZero) {
        return {
          ...base,
          title: 'Verkligt timpris (inkludera 0)',
          numerator: 'Debiterbart värde för debiterbar tid.',
          denominator: 'Alla debiterbara timmar.',
          includes: ['debit, även rader med pris 0'],
          excludes: ['alla andra units'],
          usefulFor:
            'Bra när du vill se hur 0-prissatt debiterbar tid påverkar det verkliga timpriset.',
        };
      }

      return {
        ...base,
        title: 'Verkligt timpris',
        numerator: 'Debiterbart värde för debiterbar tid med pris över 0.',
        denominator: 'Debiterbara timmar med pris över 0.',
        includes: ['debit med price > 0'],
        excludes: ['alla andra units', 'debit med price = 0'],
        usefulFor: 'Uppföljning av verkligt intjänat timpris per konsult och månad.',
      };
    }

    if (this.occupancyReportType !== 'hours') {
      return base;
    }

    const hoursModeLabel = this.getHoursModeSummaryLabel();
    const selectedModes = this.getSelectedHoursModes();
    const modeData = selectedModes.map((mode) => HOURS_MODE_DEFINITIONS[mode]);
    return {
      ...base,
      title: `Timmar: ${hoursModeLabel}`,
      description: `Visar faktiska rapporterade timmar per månad i läget "${hoursModeLabel}".`,
      includes: this.getUniqueHoursModeTexts(modeData.flatMap((mode) => mode.includes)),
      excludes: this.getUniqueHoursModeTexts(modeData.flatMap((mode) => mode.excludes)),
      usefulFor: 'Kontroll av faktisk timvolym i vald kategori per person och månad.',
    };
  }

  private refreshOccupancyPresentationState(): void {
    this.occupancyEndMonthOptions = this.dateHolidayService.getBusinessYearMonthWorkHours(
      this.occupancyStartYear
    );
    this.currentOccupancyReportHelp = this.getCurrentOccupancyReportHelp();
    this.occupancySectionTitle = this.getOccupancySectionTitle();
    this.occupancyLoadingLabel = `Laddar ${this.occupancySectionTitle.toLowerCase()}...`;
    this.occupancyEmptyState = this.getOccupancyEmptyState();
  }

  getOccupancySectionTitle(): string {
    if (this.occupancyReportType === 'bonus') {
      return 'Underlag för årsbonus per person och månad';
    }

    if (this.occupancyReportType === 'utilization') {
      switch (this.utilizationMode) {
        case 'netAfterAbsence':
          return 'Beläggning mot arbetstid efter frånvaro';
        case 'againstBudget':
          return 'Beläggning mot budgeterad tid';
        case 'budgetGapHours':
          return 'Gap mot budget i timmar';
        default:
          return 'Beläggning mot arbetstid';
      }
    }

    if (this.occupancyReportType === 'averageRate') {
      return this.occupancyAverageRateIncludeZero
        ? 'Verkligt timpris per person och månad (inkludera 0)'
        : 'Verkligt timpris per person och månad';
    }

    if (this.occupancyReportType === 'kpi') {
      return `KPI: ${this.getKpiModeLabel()} per person och månad`;
    }

    if (this.occupancyReportType === 'deviations') {
      return 'Avvikelser';
    }

    if (this.occupancyReportType === 'zeroPriceDebit') {
      return 'Debiterbar tid detalj';
    }

    if (this.occupancyReportType === 'zeroPriceProjects') {
      return 'Projekt med 0-pris tid';
    }

    if (this.occupancyReportType === 'customerMix') {
      return `Kundmix (${this.getCustomerMixViewLabel()}): ${this.getMixModeLabel()}`;
    }

    if (this.occupancyReportType === 'projectMix') {
      return `Projektmix: ${this.getMixModeLabel()}`;
    }

    if (this.occupancyReportType === 'employeeCost') {
      return 'Lönekostnad per anställd och månad';
    }

    if (this.occupancyReportType === 'absenceHours') {
      return 'Frånvarotimmar per anställd och månad';
    }

    return `${this.getHoursModeSummaryLabel()} per person och månad`;
  }

  getOccupancyLoadingLabel(): string {
    return `Laddar ${this.getOccupancySectionTitle().toLowerCase()}...`;
  }

  getOccupancyEmptyState(): string {
    if (this.occupancyReportType === 'deviations') {
      return 'Inga avvikelser hittades för vald period och aktuella filter.';
    }

    if (this.occupancyReportType === 'zeroPriceProjects') {
      return `Ingen projektsammanfattning hittades för ${this.getDebitDetailInvoiceStateLabel().toLowerCase()} debittid med prisfilter ${this.getDebitDetailPriceModeLabel().toLowerCase()} i vald period.`;
    }

    if (this.occupancyReportType === 'customerMix') {
      return `Ingen kundmix hittades för ${this.getCustomerMixViewLabel().toLowerCase()} och måttet ${this.getMixModeLabel().toLowerCase()} i vald period.`;
    }

    if (this.occupancyReportType === 'projectMix') {
      return `Ingen projektmix hittades för måttet ${this.getMixModeLabel().toLowerCase()} i vald period.`;
    }

    if (this.occupancyReportType === 'employeeCost') {
      return 'Ingen lönekostnadsdata hittades för vald period.';
    }

    if (this.occupancyReportType === 'absenceHours') {
      return 'Ingen frånvarodata hittades för vald period.';
    }

    return 'Ingen data hittades för vald period.';
  }

  hasDeviationRows(): boolean {
    return this.occupancyDeviationSections.some((section) => section.rows.length > 0);
  }

  hasOccupancyExportData(): boolean {
    return (
      this.occupancyRows.length > 0 ||
      this.occupancyZeroPriceRows.length > 0 ||
      this.occupancyProjectSummaryRows.length > 0 ||
      this.occupancyMixRows.length > 0 ||
      this.employeeCostRows.length > 0 ||
      this.absenceRows.length > 0 ||
      this.hasDeviationRows()
    );
  }

  exportOccupancyToExcel(): void {
    if (!this.hasOccupancyExportData()) {
      return;
    }

    if (this.occupancyReportType === 'zeroPriceDebit') {
      this.exportZeroPriceMatrixToExcel();
      return;
    }

    if (this.occupancyReportType === 'deviations') {
      this.exportDeviationSectionsToExcel();
      return;
    }

    if (this.occupancyReportType === 'zeroPriceProjects') {
      this.exportProjectSummaryToExcel();
      return;
    }

    if (this.occupancyReportType === 'customerMix' || this.occupancyReportType === 'projectMix') {
      this.exportMixToExcel();
      return;
    }

    if (this.occupancyReportType === 'employeeCost') {
      this.exportEmployeeCostsToExcel();
      return;
    }

    if (this.occupancyReportType === 'absenceHours') {
      this.exportAbsenceHoursToExcel();
      return;
    }

    const monthHeaders = this.occupancyMonths.map((month) => month.monthName);
    const reportTypeLabelFinal = this.getOccupancyReportTypeLabel();
    const sheetRows: Array<Array<string | number>> = [
      [this.getOccupancySectionTitle()],
      ['Rapporttyp', reportTypeLabelFinal],
      ['Verksamhetsår start', this.occupancyStartYear],
      ['Slutmånad', this.getOccupancyEndMonthLabel()],
      ...this.getOccupancyExportMetadataRows(),
      [],
      ['ID', 'Namn', 'Affärsområde', ...monthHeaders, 'Total'],
      ...this.occupancyRows.map((row) => [
        row.employee_id,
        row.name,
        this.getBusinessAreaLabel(row),
        ...this.occupancyMonths.map((month) => this.getOccupancyExportCellValue(row, month)),
        this.getOccupancyExportTotalValue(row),
      ]),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(sheetRows);
    removeBlankStringCells(worksheet);
    this.applyOccupancyExcelFormatting(worksheet, sheetRows.length, {
      firstMetricColumn: 3,
      lastMetricColumn: this.occupancyMonths.length + 3,
    });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, this.getOccupancyExportSheetName());
    const workbookBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const workbookBlob = new Blob([workbookBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    this.fileSaverService.save(workbookBlob, this.getOccupancyExportFilename());
  }

  private exportEmployeeCostsToExcel(): void {
    const monthHeaders = this.occupancyMonths.map((month) => month.monthName);
    const rows: Array<Array<string | number>> = [
      [this.getOccupancySectionTitle()],
      ['Rapporttyp', this.getOccupancyReportTypeLabel()],
      ['Verksamhetsår start', this.occupancyStartYear],
      ['Slutmånad', this.getOccupancyEndMonthLabel()],
      ...this.getOccupancyExportMetadataRows(),
      [],
      ['ID', 'Anst.nr', 'Namn', 'Affärsområde', 'Kostnadsställe', ...monthHeaders, 'Total'],
      ...this.employeeCostRows.map((row) => [
        row.employee_id,
        row.employee_number ?? '',
        row.name,
        row.business_area || '',
        row.costcenter || '',
        ...this.occupancyMonths.map((month) => this.getEmployeeCostValue(row, month) ?? ''),
        row.total ?? '',
      ]),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    removeBlankStringCells(worksheet);
    this.applyOccupancyExcelFormatting(worksheet, rows.length);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, this.getOccupancyExportSheetName());
    const workbookBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const workbookBlob = new Blob([workbookBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    this.fileSaverService.save(workbookBlob, this.getOccupancyExportFilename());
  }

  private exportAbsenceHoursToExcel(): void {
    const monthHeaders = this.occupancyMonths.map((month) => month.monthName);
    const rows: Array<Array<string | number>> = [
      [this.getOccupancySectionTitle()],
      ['Rapporttyp', this.getOccupancyReportTypeLabel()],
      ['Verksamhetsår start', this.occupancyStartYear],
      ['Slutmånad', this.getOccupancyEndMonthLabel()],
      ...this.getOccupancyExportMetadataRows(),
      [],
      [
        'ID',
        'Namn',
        'Affärsområde',
        ...monthHeaders,
        'Total frånvaro',
        'Tillgängligt',
        'Frånvaro %',
        'Sjuk',
        'VAB/NAR',
        'Ledig',
        'Semester',
        'Komp ut',
      ],
      ...this.absenceRows.map((row) => [
        row.employee_id,
        row.name,
        this.getBusinessAreaLabel(row),
        ...this.occupancyMonths.map((month) => this.getAbsenceMonthValue(row, month) ?? ''),
        row.totals.absence_hours,
        row.totals.available_hours,
        row.totals.absence_ratio ?? '',
        row.totals.sick_hours,
        row.totals.vab_hours,
        row.totals.leave_hours,
        row.totals.vacation_hours,
        row.totals.comp_out_hours,
      ]),
      [],
      [
        'Totalt',
        '',
        '',
        ...this.occupancyMonths.map((month) => this.sumAbsenceMonth(month)),
        this.absenceTotals?.absence_hours ?? '',
        this.absenceTotals?.available_hours ?? '',
        this.absenceTotals?.absence_ratio ?? '',
        this.absenceTotals?.sick_hours ?? '',
        this.absenceTotals?.vab_hours ?? '',
        this.absenceTotals?.leave_hours ?? '',
        this.absenceTotals?.vacation_hours ?? '',
        this.absenceTotals?.comp_out_hours ?? '',
      ],
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    removeBlankStringCells(worksheet);
    this.applyOccupancyExcelFormatting(worksheet, rows.length);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, this.getOccupancyExportSheetName());
    const workbookBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const workbookBlob = new Blob([workbookBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    this.fileSaverService.save(workbookBlob, this.getOccupancyExportFilename());
  }

  formatDeviationValue(value: number, unit: DeviationUnit): string {
    if (unit === 'percent') {
      return `${new Intl.NumberFormat('sv-SE', {
        useGrouping: false,
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      }).format(value * 100)} %`;
    }

    return new Intl.NumberFormat('sv-SE', {
      useGrouping: false,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  }

  private buildDeviationSections(payload: KpiMetricsPayload): DeviationSection[] {
    const monthNameByKey = new Map(
      this.occupancyMonths.map((month) => [this.getBusinessYearMonthKey(month.month), month.monthName] as const)
    );

    const createRows = (
      selector: (month: KpiMonthMetrics) => number | null | undefined,
      predicate: (value: number) => boolean,
      threshold: number
    ): DeviationRow[] => {
      return payload.rows.flatMap((row) =>
        payload.months.flatMap((monthKey) => {
          const month = row.months[monthKey];
          const value = selector(month);
          if (value == null || !predicate(value)) {
            return [];
          }
          return [
            {
              employee_id: row.employee_id,
              name: row.name,
              monthKey,
              monthName: monthNameByKey.get(monthKey) || monthKey,
              value,
              threshold,
              difference: value - threshold,
            },
          ];
        })
      );
    };

    const sections: DeviationSection[] = [
      {
        key: 'lowBudgetUtilization',
        title: 'Låg beläggning mot budget',
        description: 'Personer och månader där beläggning mot budget ligger under 90 %.',
        unit: 'percent',
        rows: createRows(
          (month) => month.utilization_budget,
          (value) => value < DEVIATION_THRESHOLDS.lowBudgetUtilization,
          DEVIATION_THRESHOLDS.lowBudgetUtilization
        ),
      },
      {
        key: 'highNetUtilization',
        title: 'Hög beläggning netto efter frånvaro',
        description: 'Personer och månader där netto-beläggningen överstiger 95 %.',
        unit: 'percent',
        rows: createRows(
          (month) => month.utilization_net_absence,
          (value) => value > DEVIATION_THRESHOLDS.highNetUtilization,
          DEVIATION_THRESHOLDS.highNetUtilization
        ),
      },
      {
        key: 'highIdleRatio',
        title: 'Hög idle-andel',
        description: 'Personer och månader där idle-andelen överstiger 15 %.',
        unit: 'percent',
        rows: createRows(
          (month) => month.idle_ratio,
          (value) => value > DEVIATION_THRESHOLDS.highIdleRatio,
          DEVIATION_THRESHOLDS.highIdleRatio
        ),
      },
      {
        key: 'highInternalRatio',
        title: 'Hög internandel',
        description: 'Personer och månader där internandelen överstiger 25 %.',
        unit: 'percent',
        rows: createRows(
          (month) => month.internal_ratio,
          (value) => value > DEVIATION_THRESHOLDS.highInternalRatio,
          DEVIATION_THRESHOLDS.highInternalRatio
        ),
      },
      {
        key: 'highZeroPriceDebitHours',
        title: 'Hög 0-pris debittid',
        description: 'Personer och månader där 0-pris debittid överstiger 8 timmar.',
        unit: 'hours',
        rows: createRows(
          (month) => month.zero_price_debit_hours,
          (value) => value > DEVIATION_THRESHOLDS.highZeroPriceDebitHours,
          DEVIATION_THRESHOLDS.highZeroPriceDebitHours
        ),
      },
      {
        key: 'highUninvoicedValue',
        title: 'Högt ofakturerat värde',
        description: 'Personer och månader där ofakturerat debiterbart värde överstiger 100000.',
        unit: 'currency',
        rows: createRows(
          (month) => month.uninvoiced_debit_value,
          (value) => value > DEVIATION_THRESHOLDS.highUninvoicedValue,
          DEVIATION_THRESHOLDS.highUninvoicedValue
        ),
      },
    ];

    return sections.map((section) => ({
      ...section,
      rows: this.expandDeviationRows(
        section.rows.sort((a, b) => {
          if (a.employee_id !== b.employee_id) {
            return a.employee_id - b.employee_id;
          }
          return this.getBusinessYearMonthIndex(a.monthKey) - this.getBusinessYearMonthIndex(b.monthKey);
        })
      ),
    }));
  }

  private exportDeviationSectionsToExcel(): void {
    const rows: Array<Array<string | number>> = [
      [this.getOccupancySectionTitle()],
      ['Rapporttyp', this.getOccupancyReportTypeLabel()],
      ['Verksamhetsår start', this.occupancyStartYear],
      ['Slutmånad', this.getOccupancyEndMonthLabel()],
      ...this.getOccupancyExportMetadataRows(),
      [],
    ];

    this.occupancyDeviationSections.forEach((section, index) => {
      if (section.rows.length === 0) {
        return;
      }
      if (index > 0 || rows[rows.length - 1].length !== 0) {
        rows.push([]);
      }
      rows.push([section.title]);
      rows.push([section.description]);
      rows.push(['ID', 'Namn', 'Månad', 'Värde', 'Gräns', 'Avvikelse']);
      section.rows.forEach((row) => {
        rows.push([
          row.employee_id,
          row.name,
          row.monthName,
          row.value,
          row.threshold,
          row.difference,
        ]);
      });
    });

    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    removeBlankStringCells(worksheet);
    this.applyDeviationExcelFormatting(worksheet, rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, this.getOccupancyExportSheetName());
    const workbookBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const workbookBlob = new Blob([workbookBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    this.fileSaverService.save(workbookBlob, this.getOccupancyExportFilename());
  }

  private applyDeviationExcelFormatting(
    worksheet: XLSX.WorkSheet,
    rows: Array<Array<string | number>>
  ): void {
    let currentUnit: DeviationUnit | null = null;

    for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
      const row = rows[rowIndex];
      if (row.length === 0) {
        currentUnit = null;
        continue;
      }

      const firstCell = row[0];
      if (typeof firstCell === 'string') {
        const matchingSection = this.occupancyDeviationSections.find((section) => section.title === firstCell);
        if (matchingSection) {
          currentUnit = matchingSection.unit;
          continue;
        }
      }

      if (row.length !== 6 || row[0] === 'ID') {
        continue;
      }

      for (let colIndex = 3; colIndex <= 5; colIndex += 1) {
        const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
        const cell = worksheet[cellRef];
        if (!cell || typeof cell.v !== 'number') {
          continue;
        }

        if (currentUnit === 'percent') {
          cell.z = '0.00%';
        } else {
          cell.z = Number.isInteger(cell.v) ? '0' : '0.00';
        }
      }
    }
  }

  calculateBudgetReport(): void {
    this.budgetMonths = this.dateHolidayService.getBusinessYearMonthWorkHours(this.budgetStartYear);
    this.budgetCalculation = this.dateHolidayService.calculateBillableBudgetByBusinessYearInputs(
      this.budgetStartYear,
      this.ownBudgetInputs,
      this.contractorBudgetInputs
    );
  }

  exportBudgetToExcel(): void {
    if (!this.budgetCalculation) {
      return;
    }

    const monthHeaders = this.budgetMonths.map((month) => month.monthName);
    const ownRows = this.getBudgetRows(this.budgetCalculation.own, this.budgetMonths, this.ownBudgetInputs);
    const contractorRows = this.getBudgetRows(
      this.budgetCalculation.contractor,
      this.budgetMonths,
      this.contractorBudgetInputs
    );

    const sheetRows: Array<Array<string | number>> = [
      ['Budgeterat Fakturerbart'],
      ['Verksamhetsår start', this.budgetStartYear],
      [],
      ['Indata', 'Egen', 'UK'],
      ['Semester', this.ownBudgetInputs.semester, this.contractorBudgetInputs.semester],
      ['Kitscon', this.ownBudgetInputs.kitscon, this.contractorBudgetInputs.kitscon],
      [
        'Kompetensutveckling',
        this.ownBudgetInputs.kompetensutveckling,
        this.contractorBudgetInputs.kompetensutveckling,
      ],
      ['Månadsmöten', this.ownBudgetInputs.månadsmöten, this.contractorBudgetInputs.månadsmöten],
      [
        'Genomsnitt sjuk',
        this.ownBudgetInputs.genomsnittSjuk,
        this.contractorBudgetInputs.genomsnittSjuk,
      ],
      [],
      ['KITS / Egen'],
      ['Rad', ...monthHeaders, 'Total'],
      ...ownRows.map((row) => [row.label, ...row.values, row.total]),
      [],
      ['UK'],
      ['Rad', ...monthHeaders, 'Total'],
      ...contractorRows.map((row) => [row.label, ...row.values, row.total]),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(sheetRows);
    removeBlankStringCells(worksheet);
    this.applyBudgetExcelFormatting(worksheet, sheetRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Budget');
    const workbookBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const workbookBlob = new Blob([workbookBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    this.fileSaverService.save(workbookBlob, `budget_fakturerbart_${this.budgetStartYear}.xlsx`);
  }

  getBudgetRows(
    breakdown: BillableBudgetBreakdown,
    months: MonthWorkHours[],
    inputs: BillableBudgetInputs
  ): Array<{ label: string; values: number[]; total: number }> {
    const toNegative = (values: number[]): number[] => values.map((value) => -value);
    const splitCombinedValues = (values: number[], partHours: number, totalHours: number): number[] => {
      if (totalHours <= 0 || partHours <= 0) {
        return values.map(() => 0);
      }

      const ratio = partHours / totalHours;
      return values.map((value) => value * ratio);
    };
    const totalCompetenceHours = inputs.kitscon + inputs.kompetensutveckling;
    const totalInternalHours = inputs.månadsmöten;
    const totalCombinedInternalHours = totalCompetenceHours + totalInternalHours;
    const combinedInternalValues = this.toBudgetValues(breakdown.internal);
    const competenceValues = splitCombinedValues(
      combinedInternalValues,
      totalCompetenceHours,
      totalCombinedInternalHours
    );
    const internalValues = splitCombinedValues(
      combinedInternalValues,
      totalInternalHours,
      totalCombinedInternalHours
    );

    return [
      {
        label: 'Antal arbetstimmar',
        values: months.map((month) => month.workHours),
        total: months.reduce((sum, month) => sum + month.workHours, 0),
      },
      {
        label: 'Semester',
        values: toNegative(this.toBudgetValues(breakdown.vacation)),
        total: -this.sumBudgetValues(breakdown.vacation),
      },
      {
        label: 'Sjuk',
        values: toNegative(this.toBudgetValues(breakdown.sick)),
        total: -this.sumBudgetValues(breakdown.sick),
      },
      {
        label: 'Kompetens',
        values: toNegative(competenceValues),
        total: -this.sumBudgetArray(competenceValues),
      },
      {
        label: 'Internt',
        values: toNegative(internalValues),
        total: -this.sumBudgetArray(internalValues),
      },
      {
        label: 'Fakturerbart',
        values: this.toBudgetValues(breakdown.billable),
        total: breakdown.yearlyTotal,
      },
    ];
  }

  formatBudgetValue(value: number): string {
    const normalizedValue = Object.is(value, -0) || Math.abs(value) < 0.005 ? 0 : value;
    return new Intl.NumberFormat('sv-SE', {
      useGrouping: false,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(normalizedValue);
  }

  formatHours(value: number): string {
    return new Intl.NumberFormat('sv-SE', {
      useGrouping: false,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  }

  formatAmount(value: number): string {
    return new Intl.NumberFormat('sv-SE', {
      useGrouping: false,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  }

  formatCustomerWithEndCustomer(
    customername?: string | null,
    endcustomer?: string | null
  ): string {
    const customer = (customername || '').trim();
    const endCustomer = (endcustomer || '').trim();

    if (endCustomer && customer && endCustomer !== customer) {
      return `${endCustomer} (${customer})`;
    }

    return endCustomer || customer;
  }

  formatCustomerProjectActivity(item: {
    customername?: string | null;
    endcustomer?: string | null;
    project_name?: string | null;
    activity_name?: string | null;
  }): string {
    return [
      this.formatCustomerWithEndCustomer(item.customername, item.endcustomer),
      item.project_name || '',
      item.activity_name || '',
    ]
      .filter((part) => !!part)
      .join(' / ');
  }

  getMonthHeaderLabel(monthName: string): string {
    const shortNames: Record<string, string> = {
      juli: 'jul',
      augusti: 'aug',
      september: 'sep',
      oktober: 'okt',
      november: 'nov',
      december: 'dec',
      januari: 'jan',
      februari: 'feb',
      mars: 'mar',
      april: 'apr',
      maj: 'maj',
      juni: 'jun',
    };

    return shortNames[monthName] || monthName;
  }

  getMatrixIdColumnCh(): number {
    const rows = this.occupancyReportType === 'zeroPriceDebit' ? this.occupancyZeroPriceRows : this.occupancyRows;
    const maxLength = Math.max(2, ...rows.map((row) => String(row.employee_id).length));
    return maxLength + 1;
  }

  getMatrixNameColumnCh(): number {
    const rows = this.occupancyReportType === 'zeroPriceDebit' ? this.occupancyZeroPriceRows : this.occupancyRows;
    const maxLength = Math.max(4, ...rows.map((row) => (row.name || '').length));
    return maxLength + 2;
  }

  private getOccupancyExportCellValue(row: OccupancyRow, month: MonthWorkHours): number | '' | null {
    if (this.isOccupancyMonthInactive(row, month)) {
      return '';
    }

    const value = this.getOccupancyValue(row, month);
    if (value === null || value === undefined) {
      return '';
    }

    return this.normalizeOccupancyValue(value);
  }

  private getOccupancyExportTotalValue(row: OccupancyRow): number | '' | null {
    if (row.total === null || row.total === undefined) {
      return '';
    }

    return this.normalizeOccupancyValue(row.total);
  }

  private getZeroPriceExportCellValue(
    row: ZeroPriceMatrixRow,
    month: MonthWorkHours
  ): string {
    if (this.isOccupancyMonthInactive(row, month)) {
      return '';
    }

    return this.formatZeroPriceCellForExport(this.getZeroPriceItems(row, month));
  }

  private getOccupancyExportMetadataRows(): Array<Array<string | number>> {
    const rows: Array<Array<string | number>> = [];

    rows.push(['Visar', this.getOccupancySectionTitle()]);
    rows.push(['Period', `${this.getOccupancyStartDate()} till ${this.getOccupancyEndDate()}`]);
    if (this.shouldUseCostcenterFilterForCurrentReport()) {
      rows.push(['Affärsområde', this.getOccupancyCostcenterLabel()]);
    }

    if (this.occupancyReportType === 'bonus') {
      rows.push(['Tak 100 %', this.occupancyBonusCapAtHundred ? 'Ja' : 'Nej']);
    }

    if (this.occupancyReportType === 'hours') {
      rows.push(['Läge', this.getHoursModeSummaryLabel()]);
      rows.push(['Valda grupper', this.getSelectedHoursModes().map((mode) => HOURS_MODE_DEFINITIONS[mode].label).join(', ')]);
    }

    if (this.occupancyReportType === 'utilization') {
      rows.push(['Beläggningssätt', this.getOccupancyModeLabel()]);
      if (this.utilizationMode === 'netAfterAbsence') {
        rows.push([
          'Fyll ut saknade timmar som tjänstledigt',
          this.occupancyUtilizationFillMissingAsLeave ? 'Ja' : 'Nej',
        ]);
      }
    }

    if (this.occupancyReportType === 'kpi') {
      rows.push(['KPI', this.getKpiModeLabel()]);
    }

    if (this.occupancyReportType === 'averageRate') {
      rows.push(['Inkludera 0 aktiviteter', this.occupancyAverageRateIncludeZero ? 'Ja' : 'Nej']);
    }

    if (this.occupancyReportType === 'absenceHours') {
      rows.push(['Frånvarogrupper', this.getAbsenceSelectionLabel() || 'Alla']);
    }

    if (this.occupancyReportType === 'zeroPriceDebit' || this.occupancyReportType === 'zeroPriceProjects') {
      rows.push(['Faktureringsstatus', this.getDebitDetailInvoiceStateLabel()]);
      rows.push(['Prisfilter', this.getDebitDetailPriceModeLabel()]);
    }

    if (this.occupancyReportType === 'customerMix' || this.occupancyReportType === 'projectMix') {
      rows.push(['Mått', this.getMixModeLabel()]);
      if (this.occupancyReportType === 'customerMix') {
        rows.push(['Kundvy', this.getCustomerMixViewLabel()]);
      }
    }

    if (this.occupancyReportType !== 'bonus') {
      rows.push(['Visa underkonsulter', this.shouldIncludeContractorsForCurrentReport() ? 'Ja' : 'Nej']);
    }

    if (this.usesEmployeeRowsForCurrentReport()) {
      rows.push([
        'Dubbla rader för P/J/H',
        this.occupancyDuplicateSelectedEmployees ? 'Ja' : 'Nej',
      ]);
    }

    return rows;
  }

  private getOccupancyExportSheetName(): string {
    switch (this.occupancyReportType) {
      case 'bonus':
        return 'Årsbonus';
      case 'utilization':
        return 'Beläggning';
      case 'kpi':
        return 'KPI';
      case 'averageRate':
        return 'Timpris';
      case 'zeroPriceDebit':
        return 'Debit detalj';
      case 'zeroPriceProjects':
        return '0-pris projekt';
      case 'customerMix':
        return 'Kundmix';
      case 'projectMix':
        return 'Projektmix';
      case 'employeeCost':
        return 'Lönekostnad per anställd';
      case 'absenceHours':
        return 'Frånvarotimmar';
      case 'deviations':
        return 'Avvikelser';
      default:
        return 'Timmar';
    }
  }

  private applyOccupancyExcelFormatting(
    worksheet: XLSX.WorkSheet,
    totalRowCount: number,
    options: { firstMetricColumn?: number; lastMetricColumn?: number } = {}
  ): void {
    const headerRowIndex = totalRowCount - this.occupancyRows.length;
    const firstDataRow = headerRowIndex + 1;
    const lastDataRow = totalRowCount - 1;
    const firstMetricColumn = options.firstMetricColumn ?? 2;
    const lastMetricColumn = options.lastMetricColumn ?? this.occupancyMonths.length + 2;

    for (let rowIndex = firstDataRow; rowIndex <= lastDataRow; rowIndex += 1) {
      for (let colIndex = firstMetricColumn; colIndex <= lastMetricColumn; colIndex += 1) {
        const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
        const cell = worksheet[cellRef];
        if (!cell || typeof cell.v !== 'number') {
          continue;
        }

        if (this.isCurrentOccupancyPercentage()) {
          cell.z = '0.00%';
        } else {
          cell.z = Number.isInteger(cell.v) ? '0' : '0.00';
        }
      }
    }
  }

  private exportZeroPriceMatrixToExcel(): void {
    const rows: Array<Array<string | number>> = [
      [this.getOccupancySectionTitle()],
      ['Rapporttyp', this.getOccupancyReportTypeLabel()],
      ['Verksamhetsår start', this.occupancyStartYear],
      ['Slutmånad', this.getOccupancyEndMonthLabel()],
      ...this.getOccupancyExportMetadataRows(),
      [],
    ];

    const headerStartRow = rows.length;
    const topHeaderRow: Array<string> = ['ID', 'Namn', 'Affärsområde'];
    const secondHeaderRow: Array<string> = ['', '', ''];

    this.occupancyMonths.forEach((month) => {
      topHeaderRow.push(month.monthName, '', '', '');
      secondHeaderRow.push('Aktivitet', 'h', 'Värde', 'Status');
    });
    topHeaderRow.push('Total');
    secondHeaderRow.push('');

    rows.push(topHeaderRow, secondHeaderRow);

    const merges: XLSX.Range[] = [
      { s: { r: headerStartRow, c: 0 }, e: { r: headerStartRow + 1, c: 0 } },
      { s: { r: headerStartRow, c: 1 }, e: { r: headerStartRow + 1, c: 1 } },
      { s: { r: headerStartRow, c: 2 }, e: { r: headerStartRow + 1, c: 2 } },
    ];

    let currentMonthStartCol = 3;
    this.occupancyMonths.forEach(() => {
      merges.push({
        s: { r: headerStartRow, c: currentMonthStartCol },
        e: { r: headerStartRow, c: currentMonthStartCol + 3 },
      });
      currentMonthStartCol += 4;
    });
    merges.push({
      s: { r: headerStartRow, c: currentMonthStartCol },
      e: { r: headerStartRow + 1, c: currentMonthStartCol },
    });

    const dataStartRow = rows.length;

    this.occupancyZeroPriceRows.forEach((row) => {
      const summaryItemsByMonth = this.occupancyMonths.map((month) =>
        this.isOccupancyMonthInactive(row, month) ? [] : this.getZeroPriceSummaryItems(row, month)
      );
      const blockRowCount = Math.max(1, ...summaryItemsByMonth.map((items) => items.length));
      const employeeStartRow = rows.length;

      for (let itemRowIndex = 0; itemRowIndex < blockRowCount; itemRowIndex += 1) {
        const exportRow: Array<string | number> = [
          itemRowIndex === 0 ? row.employee_id : '',
          itemRowIndex === 0 ? row.name : '',
          itemRowIndex === 0 ? this.getBusinessAreaLabel(row) : '',
        ];

        summaryItemsByMonth.forEach((items) => {
          const item = items[itemRowIndex];
          if (item) {
            exportRow.push(
              this.formatCustomerProjectActivity(item),
              item.hours,
              item.amount,
              item.invoice_status
            );
          } else {
            exportRow.push('', '', '', '');
          }
        });

        exportRow.push(itemRowIndex === 0 ? row.total : '');
        rows.push(exportRow);
      }

      if (blockRowCount > 1) {
        const employeeEndRow = employeeStartRow + blockRowCount - 1;
        merges.push({ s: { r: employeeStartRow, c: 0 }, e: { r: employeeEndRow, c: 0 } });
        merges.push({ s: { r: employeeStartRow, c: 1 }, e: { r: employeeEndRow, c: 1 } });
        merges.push({ s: { r: employeeStartRow, c: 2 }, e: { r: employeeEndRow, c: 2 } });
        merges.push({
          s: { r: employeeStartRow, c: currentMonthStartCol },
          e: { r: employeeEndRow, c: currentMonthStartCol },
        });
      }
    });

    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    worksheet['!merges'] = merges;
    removeBlankStringCells(worksheet);
    this.applyZeroPriceMatrixExcelFormatting(worksheet, dataStartRow, rows.length - 1);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, this.getOccupancyExportSheetName());
    const pivotRows = this.buildZeroPricePivotExportRows();
    const pivotWorksheet = XLSX.utils.aoa_to_sheet(pivotRows);
    removeBlankStringCells(pivotWorksheet);
    this.applyZeroPricePivotExcelFormatting(pivotWorksheet, pivotRows.length);
    XLSX.utils.book_append_sheet(workbook, pivotWorksheet, 'Pivotdata');
    const workbookBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const workbookBlob = new Blob([workbookBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    this.fileSaverService.save(workbookBlob, this.getOccupancyExportFilename());
  }

  private applyZeroPriceMatrixExcelFormatting(
    worksheet: XLSX.WorkSheet,
    firstDataRow: number,
    lastDataRow: number
  ): void {
    const hoursColumns: number[] = [];
    const valueColumns: number[] = [];
    let monthCol = 3;

    this.occupancyMonths.forEach(() => {
      hoursColumns.push(monthCol + 1);
      valueColumns.push(monthCol + 2);
      monthCol += 4;
    });

    const totalColumn = monthCol;

    for (let rowIndex = firstDataRow; rowIndex <= lastDataRow; rowIndex += 1) {
      hoursColumns.forEach((colIndex) => {
        const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
        const cell = worksheet[cellRef];
        if (cell && typeof cell.v === 'number') {
          cell.z = Number.isInteger(cell.v) ? '0' : '0.00';
        }
      });

      valueColumns.forEach((colIndex) => {
        const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
        const cell = worksheet[cellRef];
        if (cell && typeof cell.v === 'number') {
          cell.z = Number.isInteger(cell.v) ? '0' : '0.00';
        }
      });

      const totalCellRef = XLSX.utils.encode_cell({ r: rowIndex, c: totalColumn });
      const totalCell = worksheet[totalCellRef];
      if (totalCell && typeof totalCell.v === 'number') {
        totalCell.z = Number.isInteger(totalCell.v) ? '0' : '0.00';
      }
    }
  }

  private buildZeroPricePivotExportRows(): Array<Array<string | number>> {
    const rows: Array<Array<string | number>> = [
      [this.getOccupancySectionTitle()],
      ['Rapporttyp', this.getOccupancyReportTypeLabel()],
      ['Verksamhetsår start', this.occupancyStartYear],
      ['Slutmånad', this.getOccupancyEndMonthLabel()],
      ...this.getOccupancyExportMetadataRows(),
      [],
      [
        'ID',
        'Namn',
        'Månad',
        'Kund',
        'Projekt',
        'Aktivitet',
        'h',
        'Värde',
        'Status',
        'Total timmar person',
      ],
    ];

    this.occupancyZeroPriceRows.forEach((row) => {
      this.occupancyMonths.forEach((month) => {
        if (this.isOccupancyMonthInactive(row, month)) {
          return;
        }

        this.getZeroPriceSummaryItems(row, month).forEach((item) => {
          rows.push([
            row.employee_id,
            row.name,
            month.monthName,
            this.formatCustomerWithEndCustomer(item.customername, item.endcustomer),
            item.project_name || '',
            item.activity_name,
            item.hours,
            item.amount,
            item.invoice_status,
            row.total,
          ]);
        });
      });
    });

    return rows;
  }

  private applyZeroPricePivotExcelFormatting(
    worksheet: XLSX.WorkSheet,
    totalRowCount: number
  ): void {
    const firstDataRow = 7;
    const lastDataRow = totalRowCount - 1;
    const numericColumns = [6, 7, 9];

    for (let rowIndex = firstDataRow; rowIndex <= lastDataRow; rowIndex += 1) {
      numericColumns.forEach((colIndex) => {
        const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
        const cell = worksheet[cellRef];
        if (cell && typeof cell.v === 'number') {
          cell.z = Number.isInteger(cell.v) ? '0' : '0.00';
        }
      });
    }
  }

  private applyBudgetExcelFormatting(
    worksheet: XLSX.WorkSheet,
    sheetRows: Array<Array<string | number>>
  ): void {
    for (let rowIndex = 0; rowIndex < sheetRows.length; rowIndex += 1) {
      const row = sheetRows[rowIndex];
      for (let colIndex = 0; colIndex < row.length; colIndex += 1) {
        const value = row[colIndex];
        if (typeof value !== 'number') {
          continue;
        }
        const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
        const cell = worksheet[cellRef];
        if (!cell) {
          continue;
        }
        cell.z = Number.isInteger(value) ? '0' : '0.00';
      }
    }
  }

  private exportProjectSummaryToExcel(): void {
    const rows: Array<Array<string | number>> = [
      [this.getOccupancySectionTitle()],
      ['Rapporttyp', this.getOccupancyReportTypeLabel()],
      ['Verksamhetsår start', this.occupancyStartYear],
      ['Slutmånad', this.getOccupancyEndMonthLabel()],
      ...this.getOccupancyExportMetadataRows(),
      [],
      ['Kund', 'Projekt', 'Timmar', 'Värde', 'Antal personer', 'Antal aktiviteter', 'Första datum', 'Sista datum'],
      ...this.occupancyProjectSummaryRows.map((row) => [
        this.formatCustomerWithEndCustomer(row.customername, row.endcustomer),
        row.project_name || '',
        row.hours,
        row.amount,
        row.employee_count,
        row.activity_count,
        row.first_reporteddate || '',
        row.last_reporteddate || '',
      ]),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    removeBlankStringCells(worksheet);
    const headerIndex = rows.findIndex((row) => row[0] === 'Kund');
    for (let rowIndex = headerIndex + 1; rowIndex < rows.length; rowIndex += 1) {
      const hoursCell = worksheet[XLSX.utils.encode_cell({ r: rowIndex, c: 2 })];
      const amountCell = worksheet[XLSX.utils.encode_cell({ r: rowIndex, c: 3 })];
      const employeeCountCell = worksheet[XLSX.utils.encode_cell({ r: rowIndex, c: 4 })];
      const activityCountCell = worksheet[XLSX.utils.encode_cell({ r: rowIndex, c: 5 })];
      if (hoursCell) hoursCell.z = Number.isInteger(hoursCell.v) ? '0' : '0.00';
      if (amountCell) amountCell.z = Number.isInteger(amountCell.v) ? '0' : '0.00';
      if (employeeCountCell) employeeCountCell.z = '0';
      if (activityCountCell) activityCountCell.z = '0';
    }
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, this.getOccupancyExportSheetName());
    const workbookBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const workbookBlob = new Blob([workbookBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    this.fileSaverService.save(workbookBlob, this.getOccupancyExportFilename());
  }

  getMixValue(row: MixMatrixRow, month: MonthWorkHours): number | null {
    const key = this.getBusinessYearMonthKey(month.month);
    return row.months[key] ?? null;
  }

  getSortedMixRows(): MixMatrixRow[] {
    const rows = [...this.occupancyMixRows];
    const directionMultiplier = this.mixSortDirection === 'asc' ? 1 : -1;

    return rows.sort((a, b) => {
      const customerCompare = this
        .formatCustomerWithEndCustomer(a.customername, a.endcustomer)
        .localeCompare(this.formatCustomerWithEndCustomer(b.customername, b.endcustomer), 'sv');

      if (this.mixSortKey === 'customer') {
        if (customerCompare !== 0) {
          return customerCompare * directionMultiplier;
        }
      } else {
        const aValue =
          this.mixSortKey === 'total'
            ? a.total
            : a.months[this.mixSortKey] ?? null;
        const bValue =
          this.mixSortKey === 'total'
            ? b.total
            : b.months[this.mixSortKey] ?? null;

        const normalizedA = aValue ?? Number.NEGATIVE_INFINITY;
        const normalizedB = bValue ?? Number.NEGATIVE_INFINITY;

        if (normalizedA !== normalizedB) {
          return (normalizedA - normalizedB) * directionMultiplier;
        }

        if (customerCompare !== 0) {
          return customerCompare;
        }
      }

      return (a.group_name || '').localeCompare(b.group_name || '', 'sv');
    });
  }

  toggleMixSort(sortKey: MixSortKey): void {
    if (this.mixSortKey === sortKey) {
      this.mixSortDirection = this.mixSortDirection === 'asc' ? 'desc' : 'asc';
      return;
    }

    this.mixSortKey = sortKey;
    this.mixSortDirection = sortKey === 'customer' ? 'asc' : 'desc';
  }

  getMixSortIndicator(sortKey: MixSortKey): string {
    if (this.mixSortKey !== sortKey) {
      return '';
    }

    return this.mixSortDirection === 'asc' ? ' ↑' : ' ↓';
  }

  private exportMixToExcel(): void {
    const monthHeaders = this.occupancyMonths.map((month) => month.monthName);
    const rows: Array<Array<string | number>> = [
      [this.getOccupancySectionTitle()],
      ['Rapporttyp', this.getOccupancyReportTypeLabel()],
      ['Verksamhetsår start', this.occupancyStartYear],
      ['Slutmånad', this.getOccupancyEndMonthLabel()],
      ...this.getOccupancyExportMetadataRows(),
      [],
      this.occupancyReportType === 'projectMix'
        ? ['Kund', 'Projekt', ...monthHeaders, 'Total']
        : ['Kund', ...monthHeaders, 'Total'],
      ...this.occupancyMixRows.map((row) => [
        this.formatCustomerWithEndCustomer(row.customername, row.endcustomer),
        ...(this.occupancyReportType === 'projectMix' ? [row.group_name || ''] : []),
        ...this.occupancyMonths.map((month) => this.getMixValue(row, month) ?? ''),
        row.total ?? '',
      ]),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    removeBlankStringCells(worksheet);
    const headerOffset = 6;
    const firstMetricColumn = this.occupancyReportType === 'projectMix' ? 2 : 1;
    const lastMetricColumn = firstMetricColumn + this.occupancyMonths.length;

    for (let rowIndex = headerOffset + 1; rowIndex < rows.length; rowIndex += 1) {
      for (let colIndex = firstMetricColumn; colIndex <= lastMetricColumn; colIndex += 1) {
        const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
        const cell = worksheet[cellRef];
        if (!cell || typeof cell.v !== 'number') {
          continue;
        }
        if (this.mixMode === 'avgRate') {
          cell.z = Number.isInteger(cell.v) ? '0' : '0.00';
        } else {
          cell.z = Number.isInteger(cell.v) ? '0' : '0.00';
        }
      }
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, this.getOccupancyExportSheetName());
    const workbookBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const workbookBlob = new Blob([workbookBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    this.fileSaverService.save(workbookBlob, this.getOccupancyExportFilename());
  }

  private toApiMonthHours(monthHours: BusinessYearMonthWorkHoursMap): Record<string, number> {
    return {
      h_jul: monthHours.jul,
      h_aug: monthHours.aug,
      h_sep: monthHours.sep,
      h_okt: monthHours.okt,
      h_nov: monthHours.nov,
      h_dec: monthHours.dec,
      h_jan: monthHours.jan,
      h_feb: monthHours.feb,
      h_mar: monthHours.mar,
      h_apr: monthHours.apr,
      h_maj: monthHours.maj,
      h_jun: monthHours.jun,
    };
  }

  private mapFactsRowsToOccupancyRows(
    payload: KpiFactsPayload,
    projector: (month: KpiMonthFacts, totals: KpiMonthFacts, row: KpiFactsRow) => { month: number | null; total: number | null }
  ): OccupancyRow[] {
    return payload.rows.map((row) => {
      const mapped: OccupancyRow = {
        employee_id: row.employee_id,
        name: row.name,
        contractor: row.contractor,
        costcenter: row.costcenter,
        business_area: row.business_area,
        start_date: row.start_date,
        stop_date: row.stop_date,
        jul: null,
        aug: null,
        sep: null,
        okt: null,
        nov: null,
        dec_: null,
        jan: null,
        feb: null,
        mar: null,
        apr: null,
        maj: null,
        jun: null,
        total: null,
      };

      this.occupancyMonths.forEach((month) => {
        const monthKey = this.getBusinessYearMonthKey(month.month);
        const occupancyKey = this.getMonthKeyFromBusinessYearKey(monthKey);
        const currentMonth = row.months[monthKey];
        if (!currentMonth) {
          return;
        }
        mapped[occupancyKey] = projector(currentMonth, row.totals, row).month;
      });
      mapped.total = projector(this.buildTotalMonthFactsForProjection(row.totals), row.totals, row).total;
      return mapped;
    });
  }

  private mapMetricsRowsToOccupancyRows(
    payload: KpiMetricsPayload,
    projector: (month: KpiMonthMetrics, totals: KpiMonthMetrics, row: KpiMetricsRow) => { month: number | null; total: number | null }
  ): OccupancyRow[] {
    return payload.rows.map((row) => {
      const mapped: OccupancyRow = {
        employee_id: row.employee_id,
        name: row.name,
        contractor: row.contractor,
        costcenter: row.costcenter,
        business_area: row.business_area,
        start_date: row.start_date,
        stop_date: row.stop_date,
        jul: null,
        aug: null,
        sep: null,
        okt: null,
        nov: null,
        dec_: null,
        jan: null,
        feb: null,
        mar: null,
        apr: null,
        maj: null,
        jun: null,
        total: null,
      };

      this.occupancyMonths.forEach((month) => {
        const monthKey = this.getBusinessYearMonthKey(month.month);
        const occupancyKey = this.getMonthKeyFromBusinessYearKey(monthKey);
        const currentMonth = row.months[monthKey];
        if (!currentMonth) {
          return;
        }
        mapped[occupancyKey] = projector(currentMonth, row.totals, row).month;
      });
      mapped.total = projector(row.totals, row.totals, row).total;
      return mapped;
    });
  }

  private buildTotalMonthFactsForProjection(totals: KpiMonthFacts): KpiMonthFacts {
    return totals;
  }

  private expandOccupancyRows(rows: OccupancyRow[]): OccupancyRow[] {
    if (!this.occupancyDuplicateSelectedEmployees) {
      return rows;
    }

    return rows.flatMap((row) =>
      DUPLICATED_OCCUPANCY_EMPLOYEES.includes(row.name) ? [row, { ...row }] : [row]
    );
  }

  private shouldIncludeContractorsForCurrentReport(): boolean {
    if (this.occupancyReportType === 'bonus') {
      return false;
    }
    return this.occupancyIncludeContractors || this.isUkBusinessAreaIncludedForCurrentFilter();
  }

  private isUkBusinessAreaIncludedForCurrentFilter(): boolean {
    if (!this.shouldUseCostcenterFilterForCurrentReport()) {
      return false;
    }
    if (this.occupancyCostcentersSelected.length === 0) {
      return true;
    }
    return this.occupancyCostcentersSelected.some((externalId) => externalId === '300' || externalId === '301');
  }

  shouldUseCostcenterFilterForCurrentReport(): boolean {
    return this.usesEmployeeRowsForCurrentReport();
  }

  private usesEmployeeRowsForCurrentReport(): boolean {
    return (
      this.occupancyReportType === 'bonus' ||
      this.occupancyReportType === 'utilization' ||
      this.occupancyReportType === 'hours' ||
      this.occupancyReportType === 'averageRate' ||
      this.occupancyReportType === 'zeroPriceDebit' ||
      this.occupancyReportType === 'employeeCost' ||
      this.occupancyReportType === 'absenceHours' ||
      this.occupancyReportType === 'kpi' ||
      this.occupancyReportType === 'deviations'
    );
  }

  getBusinessAreaLabel(row: { business_area?: string | null; costcenter?: string | null }): string {
    return row.business_area || row.costcenter || '';
  }

  getOccupancyCostcenterLabel(): string {
    if (!this.occupancyCostcentersSelected.length) {
      return 'Alla affärsområden';
    }
    const labels = this.occupancyCostcentersSelected.map((externalId) => {
      const selected = this.occupancyCostcenters.find(
        (costcenter) => String(costcenter.external_id) === externalId
      );
      return selected ? selected.description : externalId;
    });
    return labels.join(', ');
  }

  getOccupancyCostcenterParam(): string {
    return this.occupancyCostcentersSelected.join(',');
  }

  isOccupancyCostcenterSelected(externalId: number | string): boolean {
    return this.occupancyCostcentersSelected.includes(String(externalId));
  }

  toggleOccupancyCostcenter(externalId: number | string, selected: boolean): void {
    const id = String(externalId);
    const next = new Set(this.occupancyCostcentersSelected);
    if (selected) {
      next.add(id);
    } else {
      next.delete(id);
    }
    const sortedIds = this.occupancyCostcenters.map((costcenter) => String(costcenter.external_id));
    this.occupancyCostcentersSelected = sortedIds.filter((candidate) => next.has(candidate));
    this.loadOccupancyReport();
  }

  clearOccupancyCostcenters(): void {
    if (!this.occupancyCostcentersSelected.length) {
      return;
    }
    this.occupancyCostcentersSelected = [];
    this.loadOccupancyReport();
  }

  private getOccupancyModeLabel(): string {
    if (this.occupancyReportType === 'bonus') {
      return 'Underlag för årsbonus';
    }

    if (this.occupancyReportType === 'utilization') {
      switch (this.utilizationMode) {
        case 'netAfterAbsence':
          return 'Mot arbetstid efter frånvaro';
        case 'againstBudget':
          return 'Mot budgeterad tid';
        case 'budgetGapHours':
          return 'Gap mot budget (timmar)';
        default:
          return 'Mot arbetstid';
      }
    }

    if (this.occupancyReportType === 'kpi') {
      return this.getKpiModeLabel();
    }

    if (this.occupancyReportType === 'deviations') {
      return 'Avvikelser';
    }

    if (this.occupancyReportType === 'averageRate') {
      return this.occupancyAverageRateIncludeZero ? 'Verkligt timpris (inkludera 0)' : 'Verkligt timpris';
    }

    if (this.occupancyReportType === 'zeroPriceDebit') {
      return `Debiterbar tid detalj (${this.getDebitDetailInvoiceStateLabel()}, ${this.getDebitDetailPriceModeLabel()})`;
    }

    if (this.occupancyReportType === 'zeroPriceProjects') {
      return `Projekt med 0-pris tid (${this.getDebitDetailInvoiceStateLabel()}, ${this.getDebitDetailPriceModeLabel()})`;
    }

    if (this.occupancyReportType === 'customerMix') {
      return `Kundmix (${this.getCustomerMixViewLabel()}, ${this.getMixModeLabel()})`;
    }

    if (this.occupancyReportType === 'projectMix') {
      return `Projektmix (${this.getMixModeLabel()})`;
    }

    if (this.occupancyReportType === 'absenceHours') {
      return this.getAbsenceSelectionLabel()
        ? `Frånvarotimmar: ${this.getAbsenceSelectionLabel()}`
        : 'Frånvarotimmar';
    }

    return this.getHoursModeSummaryLabel();
  }

  private getSelectedHoursModes(): OccupancyMode[] {
    const selected = Array.isArray(this.occupancyModes) ? this.occupancyModes : [];
    return selected.length > 0 ? selected : ['allHours'];
  }

  private getSelectedHoursValue(month: KpiMonthFacts): number | null {
    const selectedModes = this.getSelectedHoursModes();
    const sum = selectedModes.reduce((total, mode) => {
      const field = HOURS_MODE_DEFINITIONS[mode]?.field ?? 'all_hours';
      return total + Number((month[field] as number | null | undefined) ?? 0);
    }, 0);
    return sum;
  }

  private expandAbsenceRows(rows: AbsenceRow[]): AbsenceRow[] {
    if (!this.occupancyDuplicateSelectedEmployees) {
      return rows;
    }

    return rows.flatMap((row) =>
      DUPLICATED_OCCUPANCY_EMPLOYEES.includes(row.name) ? [row, { ...row }] : [row]
    );
  }

  private getHoursModeSummaryLabel(): string {
    const selectedModes = this.getSelectedHoursModes();
    if (selectedModes.length === 1) {
      return HOURS_MODE_DEFINITIONS[selectedModes[0]]?.label || HOURS_MODE_DEFINITIONS.allHours.label;
    }

    return `${selectedModes.length} valda grupper`;
  }

  private getUniqueHoursModeTexts(values: string[]): string[] {
    return Array.from(new Set(values));
  }

  private getOccupancyReportTypeLabel(): string {
    switch (this.occupancyReportType) {
      case 'bonus':
        return 'Underlag för årsbonus';
      case 'utilization':
        return 'Beläggning';
      case 'kpi':
        return 'KPI';
      case 'averageRate':
        return 'Verkligt timpris';
      case 'zeroPriceDebit':
        return 'Debiterbar tid detalj';
      case 'zeroPriceProjects':
        return 'Projekt med 0-pris tid';
      case 'customerMix':
        return 'Kundmix';
      case 'projectMix':
        return 'Projektmix';
      case 'absenceHours':
        return 'Frånvarotimmar';
      case 'deviations':
        return 'Avvikelser';
      default:
        return 'Timmar';
    }
  }

  private getOccupancyExportFilename(): string {
    const prefix =
      this.occupancyReportType === 'bonus'
        ? 'bonus'
        : this.occupancyReportType === 'utilization'
          ? 'belaggning'
        : this.occupancyReportType === 'kpi'
          ? 'kpi'
        : this.occupancyReportType === 'averageRate'
          ? 'verkligt_timpris'
          : this.occupancyReportType === 'deviations'
            ? 'avvikelser'
          : this.occupancyReportType === 'zeroPriceProjects'
            ? 'projekt_med_0pris_tid'
          : this.occupancyReportType === 'customerMix'
            ? 'kundmix'
          : this.occupancyReportType === 'projectMix'
            ? 'projektmix'
          : this.occupancyReportType === 'employeeCost'
            ? 'lonekostnad_per_anstalld'
          : this.occupancyReportType === 'absenceHours'
            ? 'franvarotimmar'
          : this.occupancyReportType === 'zeroPriceDebit'
            ? 'debiterbar_tid_detalj'
          : 'timmar';
    const modeSuffix =
      this.occupancyReportType !== 'hours' &&
      this.occupancyReportType !== 'utilization' &&
      this.occupancyReportType !== 'kpi' &&
      this.occupancyReportType !== 'customerMix' &&
      this.occupancyReportType !== 'projectMix'
        ? ''
        : `_${this.getOccupancyModeLabel()
            .toLowerCase()
            .replace(/å/g, 'a')
            .replace(/ä/g, 'a')
            .replace(/ö/g, 'o')
            .replace(/\s+/g, '_')}`;

    return `${prefix}${modeSuffix}_${this.occupancyStartYear}.xlsx`;
  }

  getMixModeLabel(): string {
    switch (this.mixMode) {
      case 'hours':
        return 'Debiterbara timmar';
      case 'avgRate':
        return 'Verkligt timpris';
      default:
        return 'Debiterbart värde';
    }
  }

  getCustomerMixViewLabel(): string {
    return this.customerMixView === 'endcustomer' ? 'Slutkund' : 'Fakturakund';
  }

  private getKpiFamily(): 'capacity' | 'value' | 'pipeline' {
    switch (this.kpiMode) {
      case 'debitValue':
      case 'valuePerAvailableHour':
      case 'valuePerNetHour':
        return 'value';
      case 'invoicedHoursRatio':
      case 'invoicedValueRatio':
      case 'uninvoicedHoursRatio':
      case 'uninvoicedValueRatio':
      case 'zeroPriceDebitRatio':
      case 'invoicedDebitHours':
      case 'invoicedDebitValue':
      case 'uninvoicedDebitHours':
      case 'uninvoicedDebitValue':
      case 'zeroPriceDebitHours':
        return 'pipeline';
      default:
        return 'capacity';
    }
  }

  private getKpiMetricValue(metric: KpiMonthMetrics): number | null {
    switch (this.kpiMode) {
      case 'absenceRatio':
        return metric.absence_ratio ?? null;
      case 'idleRatio':
        return metric.idle_ratio ?? null;
      case 'ohRatio':
        return metric.oh_ratio ?? null;
      case 'internalProjectRatio':
        return metric.internal_project_ratio ?? null;
      case 'debitValue':
        return metric.debit_value ?? null;
      case 'valuePerAvailableHour':
        return metric.value_per_available_hour ?? null;
      case 'valuePerNetHour':
        return metric.value_per_net_hour ?? null;
      case 'invoicedHoursRatio':
        return metric.invoiced_hours_ratio ?? null;
      case 'invoicedValueRatio':
        return metric.invoiced_value_ratio ?? null;
      case 'uninvoicedHoursRatio':
        return metric.uninvoiced_hours_ratio ?? null;
      case 'uninvoicedValueRatio':
        return metric.uninvoiced_value_ratio ?? null;
      case 'zeroPriceDebitRatio':
        return metric.zero_price_debit_ratio ?? null;
      case 'invoicedDebitHours':
        return metric.invoiced_debit_hours ?? null;
      case 'invoicedDebitValue':
        return metric.invoiced_debit_value ?? null;
      case 'uninvoicedDebitHours':
        return metric.uninvoiced_debit_hours ?? null;
      case 'uninvoicedDebitValue':
        return metric.uninvoiced_debit_value ?? null;
      case 'zeroPriceDebitHours':
        return metric.zero_price_debit_hours ?? null;
      default:
        return metric.internal_ratio ?? null;
    }
  }

  private isCurrentKpiRatio(): boolean {
    switch (this.kpiMode) {
      case 'internalRatio':
      case 'absenceRatio':
      case 'idleRatio':
      case 'ohRatio':
      case 'internalProjectRatio':
      case 'invoicedHoursRatio':
      case 'invoicedValueRatio':
      case 'uninvoicedHoursRatio':
      case 'uninvoicedValueRatio':
      case 'zeroPriceDebitRatio':
        return true;
      default:
        return false;
    }
  }

  getKpiModeLabel(): string {
    switch (this.kpiMode) {
      case 'absenceRatio':
        return 'Frånvaroandel';
      case 'idleRatio':
        return 'Idle-andel';
      case 'ohRatio':
        return 'OH-andel';
      case 'internalProjectRatio':
        return 'Kompetens-/internprojektandel';
      case 'debitValue':
        return 'Debiterbart värde';
      case 'valuePerAvailableHour':
        return 'Värde per tillgänglig timme';
      case 'valuePerNetHour':
        return 'Värde per närvarotimme';
      case 'invoicedHoursRatio':
        return 'Faktureringsgrad timmar';
      case 'invoicedValueRatio':
        return 'Faktureringsgrad värde';
      case 'uninvoicedHoursRatio':
        return 'Ofaktureringsgrad timmar';
      case 'uninvoicedValueRatio':
        return 'Ofaktureringsgrad värde';
      case 'zeroPriceDebitRatio':
        return '0-pris andel av debittid';
      case 'invoicedDebitHours':
        return 'Fakturerade debiterbara timmar';
      case 'invoicedDebitValue':
        return 'Fakturerat debiterbart värde';
      case 'uninvoicedDebitHours':
        return 'Ofakturerade debiterbara timmar';
      case 'uninvoicedDebitValue':
        return 'Ofakturerat debiterbart värde';
      case 'zeroPriceDebitHours':
        return '0-pris debittid';
      default:
        return 'Internandel';
    }
  }

  private isCurrentOccupancyPercentage(): boolean {
    if (this.occupancyReportType === 'bonus') {
      return true;
    }

    if (this.occupancyReportType === 'utilization') {
      return this.utilizationMode !== 'budgetGapHours';
    }

    if (this.occupancyReportType === 'kpi') {
      return this.isCurrentKpiRatio();
    }

    return false;
  }

  private loadUtilizationRows() {
    const monthHours = this.dateHolidayService.getBusinessYearMonthWorkHoursMap(this.occupancyStartYear);
    let baseParams = new HttpParams()
      .set('includeContractors', String(this.shouldIncludeContractorsForCurrentReport()))
      .set('excludeInternal', 'false')
      .set('endMonth', String(this.occupancyEndMonth));
    const costcenterParam = this.getOccupancyCostcenterParam();
    if (costcenterParam) {
      baseParams = baseParams.set('costcenter', costcenterParam);
    }

    const withMonthHours = (params: HttpParams, mode: OccupancyMode) => {
      let next = params.set('mode', mode);
      Object.entries(this.toApiMonthHours(monthHours)).forEach(([key, value]) => {
        next = next.set(key, String(value));
      });
      return next;
    };

    return forkJoin({
      debit: this.http.get<OccupancyRow[]>(`/api/salary/occupancy/${this.occupancyStartYear}`, {
        params: withMonthHours(baseParams, 'debitHours'),
      }),
      sick: this.http.get<OccupancyRow[]>(`/api/salary/occupancy/${this.occupancyStartYear}`, {
        params: withMonthHours(baseParams, 'sickHours'),
      }),
      leave: this.http.get<OccupancyRow[]>(`/api/salary/occupancy/${this.occupancyStartYear}`, {
        params: withMonthHours(baseParams, 'leaveHours'),
      }),
      vacation: this.http.get<OccupancyRow[]>(`/api/salary/occupancy/${this.occupancyStartYear}`, {
        params: withMonthHours(baseParams, 'vacationHours'),
      }),
    }).pipe();
  }

  private buildUtilizationRows(source: {
    debit: OccupancyRow[];
    sick: OccupancyRow[];
    leave: OccupancyRow[];
    vacation: OccupancyRow[];
  }): OccupancyRow[] {
    const monthHours = this.dateHolidayService.getBusinessYearMonthWorkHoursMap(this.occupancyStartYear);
    const budgetCalculation = this.dateHolidayService.calculateBillableBudgetByBusinessYearInputs(
      this.occupancyStartYear,
      this.ownBudgetInputs,
      this.contractorBudgetInputs
    );
    const allRows = [...source.debit, ...source.sick, ...source.leave, ...source.vacation];
    const rowMap = new Map<number, OccupancyRow>();

    allRows.forEach((row) => {
      if (!rowMap.has(row.employee_id)) {
        rowMap.set(row.employee_id, {
          employee_id: row.employee_id,
          name: row.name,
          contractor: (row as any).contractor,
          jul: null,
          aug: null,
          sep: null,
          okt: null,
          nov: null,
          dec_: null,
          jan: null,
          feb: null,
          mar: null,
          apr: null,
          maj: null,
          jun: null,
          total: null,
        } as OccupancyRow & { contractor?: string | null });
      }
    });

    const visibleMonthKeys = this.occupancyMonths.map((month) =>
      this.getBusinessYearMonthKey(month.month)
    );
    const workHoursArray = this.toBudgetValues(monthHours);

    const debitMap = this.toEmployeeValueMap(source.debit);
    const sickMap = this.toEmployeeValueMap(source.sick);
    const leaveMap = this.toEmployeeValueMap(source.leave);
    const vacationMap = this.toEmployeeValueMap(source.vacation);

    rowMap.forEach((targetRow, employeeId) => {
      const isContractor = ['y', 'Y', 'j', 'J'].includes(String((targetRow as any).contractor || ''));
      const budget = isContractor ? budgetCalculation.contractor.billable : budgetCalculation.own.billable;
      const budgetValues = this.toBudgetValues(budget);

      let totalNumerator = 0;
      let totalDenominator = 0;
      let totalGap = 0;

      visibleMonthKeys.forEach((monthKey) => {
        const index = this.getBusinessYearMonthIndex(monthKey);
        const debit = this.getMappedMonthValue(debitMap, employeeId, monthKey);
        const sick = this.getMappedMonthValue(sickMap, employeeId, monthKey);
        const leave = this.getMappedMonthValue(leaveMap, employeeId, monthKey);
        const vacation = this.getMappedMonthValue(vacationMap, employeeId, monthKey);
        const workHours = workHoursArray[index];
        const budgetHours = budgetValues[index];
        const implicitLeave = this.occupancyUtilizationFillMissingAsLeave
          ? Math.max(0, workHours - (debit + sick + leave + vacation))
          : 0;

        let value: number | null;
        let denominator: number;

        switch (this.utilizationMode) {
          case 'netAfterAbsence':
            denominator = workHours - sick - leave - vacation - implicitLeave;
            value = denominator > 0 ? debit / denominator : 0;
            break;
          case 'againstBudget':
            denominator = budgetHours;
            value = denominator > 0 ? debit / denominator : null;
            break;
          case 'budgetGapHours':
            value = debit - budgetHours;
            denominator = 0;
            totalGap += value;
            break;
          default:
            denominator = workHours;
            value = denominator > 0 ? debit / denominator : null;
            break;
        }

        (targetRow as any)[this.getMonthKeyFromBusinessYearKey(monthKey)] = value;
        totalNumerator += debit;
        if (this.utilizationMode === 'gross') {
          totalDenominator += workHours;
        } else if (this.utilizationMode === 'netAfterAbsence') {
          totalDenominator += Math.max(0, workHours - sick - leave - vacation - implicitLeave);
        } else if (this.utilizationMode === 'againstBudget') {
          totalDenominator += budgetHours;
        }
      });

      if (this.utilizationMode === 'budgetGapHours') {
        targetRow.total = totalGap;
      } else {
        targetRow.total = totalDenominator > 0 ? totalNumerator / totalDenominator : 0;
      }
    });

    return Array.from(rowMap.values()).sort((a, b) => a.employee_id - b.employee_id);
  }

  private toEmployeeValueMap(rows: OccupancyRow[]): Map<number, Record<keyof BusinessYearMonthValueMap, number>> {
    const map = new Map<number, Record<keyof BusinessYearMonthValueMap, number>>();
    rows.forEach((row) => {
      map.set(row.employee_id, {
        jul: row.jul ?? 0,
        aug: row.aug ?? 0,
        sep: row.sep ?? 0,
        okt: row.okt ?? 0,
        nov: row.nov ?? 0,
        dec: row.dec_ ?? 0,
        jan: row.jan ?? 0,
        feb: row.feb ?? 0,
        mar: row.mar ?? 0,
        apr: row.apr ?? 0,
        maj: row.maj ?? 0,
        jun: row.jun ?? 0,
      });
    });
    return map;
  }

  private getMappedMonthValue(
    map: Map<number, Record<keyof BusinessYearMonthValueMap, number>>,
    employeeId: number,
    monthKey: keyof BusinessYearMonthValueMap
  ): number {
    return map.get(employeeId)?.[monthKey] ?? 0;
  }

  private getMonthKeyFromBusinessYearKey(monthKey: keyof BusinessYearMonthValueMap): keyof Pick<
    OccupancyRow,
    'jul' | 'aug' | 'sep' | 'okt' | 'nov' | 'dec_' | 'jan' | 'feb' | 'mar' | 'apr' | 'maj' | 'jun'
  > {
    switch (monthKey) {
      case 'jul':
        return 'jul';
      case 'aug':
        return 'aug';
      case 'sep':
        return 'sep';
      case 'okt':
        return 'okt';
      case 'nov':
        return 'nov';
      case 'dec':
        return 'dec_';
      case 'jan':
        return 'jan';
      case 'feb':
        return 'feb';
      case 'mar':
        return 'mar';
      case 'apr':
        return 'apr';
      case 'maj':
        return 'maj';
      case 'jun':
        return 'jun';
      default:
        throw new Error(`Unsupported business year month key: ${monthKey}`);
    }
  }

  private getBusinessYearMonthKey(month: number): keyof BusinessYearMonthValueMap {
    switch (month) {
      case 7:
        return 'jul';
      case 8:
        return 'aug';
      case 9:
        return 'sep';
      case 10:
        return 'okt';
      case 11:
        return 'nov';
      case 12:
        return 'dec';
      case 1:
        return 'jan';
      case 2:
        return 'feb';
      case 3:
        return 'mar';
      case 4:
        return 'apr';
      case 5:
        return 'maj';
      case 6:
        return 'jun';
      default:
        throw new Error(`Unsupported month for business year key: ${month}`);
    }
  }

  private getBusinessYearMonthIndex(monthKey: keyof BusinessYearMonthValueMap): number {
    const order: Array<keyof BusinessYearMonthValueMap> = [
      'jul',
      'aug',
      'sep',
      'okt',
      'nov',
      'dec',
      'jan',
      'feb',
      'mar',
      'apr',
      'maj',
      'jun',
    ];
    return order.indexOf(monthKey);
  }

  getOccupancyEndMonthOptions(): MonthWorkHours[] {
    return this.occupancyEndMonthOptions;
  }

  getOccupancyEndMonthLabel(): string {
    return this.getOccupancyEndMonthOptions().find((month) => month.month === this.occupancyEndMonth)?.monthName || '';
  }

  private getVisibleOccupancyMonths(): MonthWorkHours[] {
    const months = this.dateHolidayService.getBusinessYearMonthWorkHours(this.occupancyStartYear);
    const endIndex = months.findIndex((month) => month.month === this.occupancyEndMonth);
    return endIndex >= 0 ? months.slice(0, endIndex + 1) : months;
  }

  private getVisibleBusinessYearMonthWorkHoursMap(): BusinessYearMonthWorkHoursMap {
    const full = this.dateHolidayService.getBusinessYearMonthWorkHoursMap(this.occupancyStartYear);
    const visibleKeys = new Set(this.getVisibleOccupancyMonths().map((month) => this.getBusinessYearMonthKey(month.month)));

    return {
      jul: visibleKeys.has('jul') ? full.jul : 0,
      aug: visibleKeys.has('aug') ? full.aug : 0,
      sep: visibleKeys.has('sep') ? full.sep : 0,
      okt: visibleKeys.has('okt') ? full.okt : 0,
      nov: visibleKeys.has('nov') ? full.nov : 0,
      dec: visibleKeys.has('dec') ? full.dec : 0,
      jan: visibleKeys.has('jan') ? full.jan : 0,
      feb: visibleKeys.has('feb') ? full.feb : 0,
      mar: visibleKeys.has('mar') ? full.mar : 0,
      apr: visibleKeys.has('apr') ? full.apr : 0,
      maj: visibleKeys.has('maj') ? full.maj : 0,
      jun: visibleKeys.has('jun') ? full.jun : 0,
    };
  }

  private getOccupancyStartDate(): string {
    return `${this.occupancyStartYear}-07-01`;
  }

  private getOccupancyEndDate(): string {
    const endYear = this.occupancyEndMonth >= 7 ? this.occupancyStartYear : this.occupancyStartYear + 1;
    return moment({ year: endYear, month: this.occupancyEndMonth - 1, day: 1 }).endOf('month').format('YYYY-MM-DD');
  }

  getDebitDetailInvoiceStateLabel(): string {
    switch (this.debitDetailInvoiceState) {
      case 'invoiced':
        return 'Fakturerat';
      case 'all':
        return 'Båda';
      default:
        return 'Ofakturerat';
    }
  }

  getDebitDetailPriceModeLabel(): string {
    switch (this.debitDetailPriceMode) {
      case 'positive':
        return '> 0';
      case 'all':
        return 'Båda';
      default:
        return '0';
    }
  }

  private toBudgetValues(values: {
    jul: number;
    aug: number;
    sep: number;
    okt: number;
    nov: number;
    dec: number;
    jan: number;
    feb: number;
    mar: number;
    apr: number;
    maj: number;
    jun: number;
  }): number[] {
    return [
      values.jul,
      values.aug,
      values.sep,
      values.okt,
      values.nov,
      values.dec,
      values.jan,
      values.feb,
      values.mar,
      values.apr,
      values.maj,
      values.jun,
    ];
  }

  private sumBudgetValues(values: {
    jul: number;
    aug: number;
    sep: number;
    okt: number;
    nov: number;
    dec: number;
    jan: number;
    feb: number;
    mar: number;
    apr: number;
    maj: number;
    jun: number;
  }): number {
    return (
      values.jul +
      values.aug +
      values.sep +
      values.okt +
      values.nov +
      values.dec +
      values.jan +
      values.feb +
      values.mar +
      values.apr +
      values.maj +
      values.jun
    );
  }

  private sumBudgetArray(values: number[]): number {
    return values.reduce((sum, value) => sum + value, 0);
  }

  private getMonthKey(month: number): keyof Pick<
    OccupancyRow,
    'jul' | 'aug' | 'sep' | 'okt' | 'nov' | 'dec_' | 'jan' | 'feb' | 'mar' | 'apr' | 'maj' | 'jun'
  > {
    switch (month) {
      case 7:
        return 'jul';
      case 8:
        return 'aug';
      case 9:
        return 'sep';
      case 10:
        return 'okt';
      case 11:
        return 'nov';
      case 12:
        return 'dec_';
      case 1:
        return 'jan';
      case 2:
        return 'feb';
      case 3:
        return 'mar';
      case 4:
        return 'apr';
      case 5:
        return 'maj';
      case 6:
        return 'jun';
      default:
        throw new Error(`Unsupported month: ${month}`);
    }
  }

  private getMonthDateRange(month: MonthWorkHours): { from: string; to: string } {
    const year = month.month >= 7 ? this.occupancyStartYear : this.occupancyStartYear + 1;
    const fromMoment = moment({ year, month: month.month - 1, day: 1 });
    const toMoment = fromMoment.clone().endOf('month');
    return {
      from: fromMoment.format('YYYY-MM-DD'),
      to: toMoment.format('YYYY-MM-DD'),
    };
  }

  private getVisiblePeriodDateRange(): { from: string; to: string } {
    return {
      from: `${this.occupancyStartYear}-07-01`,
      to: this.getOccupancyEndDate(),
    };
  }

  private getVisiblePeriodLabel(): string {
    const range = this.getVisiblePeriodDateRange();
    return `${range.from} till ${range.to}`;
  }

  getZeroPriceItems(row: ZeroPriceMatrixRow, month: MonthWorkHours): ZeroPriceCellItem[] {
    const key = this.getMonthKey(month.month);
    return row[key] as ZeroPriceCellItem[];
  }

  getZeroPriceSummaryItems(row: ZeroPriceMatrixRow, month: MonthWorkHours): ZeroPriceCellSummaryItem[] {
    const items = this.getZeroPriceItems(row, month);
    const grouped = new Map<string, ZeroPriceCellSummaryItem>();

    items.forEach((item) => {
      const key = `${item.customername || ''}__${item.endcustomer || ''}__${item.project_name || ''}__${item.activity_name}__${item.price}__${item.invoice_status}`;
      const existing = grouped.get(key);
      if (existing) {
        existing.hours += item.hours;
        existing.amount += item.amount;
        return;
      }

      grouped.set(key, {
        customername: item.customername,
        endcustomer: item.endcustomer,
        project_name: item.project_name,
        activity_name: item.activity_name,
        price: item.price,
        invoice_status: item.invoice_status,
        hours: item.hours,
        amount: item.amount,
      });
    });

    return Array.from(grouped.values()).sort((a, b) => {
      const customerCompare = this
        .formatCustomerWithEndCustomer(a.customername, a.endcustomer)
        .localeCompare(this.formatCustomerWithEndCustomer(b.customername, b.endcustomer), 'sv');
      if (customerCompare !== 0) return customerCompare;
      const projectCompare = (a.project_name || '').localeCompare(b.project_name || '', 'sv');
      if (projectCompare !== 0) return projectCompare;
      const activityCompare = a.activity_name.localeCompare(b.activity_name, 'sv');
      if (activityCompare !== 0) return activityCompare;
      const priceCompare = a.price - b.price;
      if (priceCompare !== 0) return priceCompare;
      return a.invoice_status.localeCompare(b.invoice_status, 'sv');
    });
  }

  getZeroPriceTotalItems(row: ZeroPriceMatrixRow): string {
    return this.formatHours(row.total);
  }

  private mapZeroPriceRowsToMatrix(rows: UninvoicedZeroPriceDebitRow[]): ZeroPriceMatrixRow[] {
    const byEmployee = new Map<number, ZeroPriceMatrixRow>();

    rows.forEach((row) => {
      const existing = byEmployee.get(row.employee_id);
      const item: ZeroPriceCellItem = {
        reporteddate: row.reporteddate,
        customername: row.customername,
        endcustomer: row.endcustomer,
        project_name: row.project_name,
        activity_name: row.activity_name,
        price: row.price,
        invoice_status: row.invoice_status,
        hours: row.hours,
        amount: row.amount,
      };

      if (existing) {
        const monthKey = this.getMonthKey(row.month);
        (existing[monthKey] as ZeroPriceCellItem[]).push(item);
        existing.total += row.hours;
        return;
      }

      const newRow: ZeroPriceMatrixRow = {
        employee_id: row.employee_id,
        name: row.name,
        costcenter: row.costcenter,
        business_area: row.business_area,
        start_date: row.start_date,
        stop_date: row.stop_date,
        jul: [],
        aug: [],
        sep: [],
        okt: [],
        nov: [],
        dec_: [],
        jan: [],
        feb: [],
        mar: [],
        apr: [],
        maj: [],
        jun: [],
        total: row.hours,
      };

      const monthKey = this.getMonthKey(row.month);
      (newRow[monthKey] as ZeroPriceCellItem[]).push(item);
      byEmployee.set(row.employee_id, newRow);
    });

    return Array.from(byEmployee.values()).sort((a, b) => a.employee_id - b.employee_id);
  }

  private expandZeroPriceMatrixRows(rows: ZeroPriceMatrixRow[]): ZeroPriceMatrixRow[] {
    if (!this.occupancyDuplicateSelectedEmployees) {
      return rows;
    }

    return rows.flatMap((row) =>
      DUPLICATED_OCCUPANCY_EMPLOYEES.includes(row.name) ? [row, { ...row }] : [row]
    );
  }

  private expandDeviationRows(rows: DeviationRow[]): DeviationRow[] {
    if (!this.occupancyDuplicateSelectedEmployees) {
      return rows;
    }

    return rows.flatMap((row) =>
      DUPLICATED_OCCUPANCY_EMPLOYEES.includes(row.name) ? [row, { ...row }] : [row]
    );
  }

  private formatZeroPriceCellForExport(items: ZeroPriceCellItem[]): string {
    const grouped = new Map<string, ZeroPriceCellSummaryItem>();

    items.forEach((item) => {
      const key = `${item.customername || ''}__${item.endcustomer || ''}__${item.project_name || ''}__${item.activity_name}__${item.price}__${item.invoice_status}`;
      const existing = grouped.get(key);
      if (existing) {
        existing.hours += item.hours;
        existing.amount += item.amount;
        return;
      }

      grouped.set(key, {
        customername: item.customername,
        endcustomer: item.endcustomer,
        project_name: item.project_name,
        activity_name: item.activity_name,
        price: item.price,
        invoice_status: item.invoice_status,
        hours: item.hours,
        amount: item.amount,
      });
    });

    return Array.from(grouped.values())
      .map(
        (item) =>
          `${this.formatCustomerProjectActivity(item)} | ${this.formatHours(item.hours)} | ${this.formatAmount(item.amount)} | ${item.invoice_status}`
      )
      .join('\n');
  }
}
